import React, { useState, useMemo, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  CRow,
  CCol,
  CCard,
  CCardHeader,
  CCardBody,
  CListGroup,
  CListGroupItem,
  CBadge,
  CButton,
  CFormCheck,
  CFormSwitch,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import {
  cilInfo,
  cilChevronBottom,
  cilChevronTop,
  cilWarning,
  cilTruck,
  cilChartLine,
  cilSettings,
  cilReload,
  cilArrowTop,
  cilArrowBottom,
} from "@coreui/icons";
import { ResponsiveBar } from "@nivo/bar";
import { ResponsiveLine } from "@nivo/line";
import { getFakeLocalDate } from "../utils/timeUtils";
import SafeTooltip from "../../../components/SafeTooltip/SafeTooltip";
import DowntimeModal from "./DowntimeModal";

const ZoomableNivoWrapper = ({ dataLength, minWindow = 5, children }) => {
  const [bounds, setBounds] = React.useState({ start: 0, end: Math.max(0, dataLength - 1) });
  const containerRef = React.useRef(null);
  const isPanning = React.useRef(false);
  const lastX = React.useRef(0);

  React.useEffect(() => {
    setBounds({ start: 0, end: Math.max(0, dataLength - 1) });
  }, [dataLength]);

  React.useEffect(() => {
    const container = containerRef.current;
    if (!container || dataLength <= 1) return;

    const handleWheel = (e) => {
      e.preventDefault();
      const rect = container.getBoundingClientRect();
      const pct = (e.clientX - rect.left) / rect.width;

      setBounds((prev) => {
        const curLen = prev.end - prev.start;
        const factor = e.deltaY > 0 ? 1.25 : 0.8;
        let newLen = curLen * factor;

        const actualMin = Math.min(minWindow, dataLength - 1);
        if (newLen < actualMin) newLen = actualMin;
        if (newLen >= dataLength - 1) newLen = dataLength - 1;

        const anchor = prev.start + curLen * pct;
        let newStart = anchor - newLen * pct;
        let newEnd = newStart + newLen;

        if (newStart < 0) {
          newStart = 0;
          newEnd = Math.min(newStart + newLen, dataLength - 1);
        }
        if (newEnd > dataLength - 1) {
          newEnd = dataLength - 1;
          newStart = Math.max(0, newEnd - newLen);
        }

        return { start: newStart, end: newEnd };
      });
    };

    container.addEventListener("wheel", handleWheel, { passive: false });
    return () => container.removeEventListener("wheel", handleWheel);
  }, [dataLength, minWindow]);

  const onPointerDown = (e) => {
    if (dataLength <= 1) return;
    isPanning.current = true;
    lastX.current = e.clientX;
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e) => {
    if (!isPanning.current || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const deltaX = e.clientX - lastX.current;
    lastX.current = e.clientX;

    setBounds((prev) => {
      const curLen = prev.end - prev.start;
      const shift = -(deltaX / rect.width) * curLen;

      let newStart = prev.start + shift;
      let newEnd = prev.end + shift;

      if (newStart < 0) {
        newStart = 0;
        newEnd = curLen;
      }
      if (newEnd > dataLength - 1) {
        newEnd = dataLength - 1;
        newStart = Math.max(0, newEnd - curLen);
      }
      return { start: newStart, end: newEnd };
    });
  };

  const onPointerUp = (e) => {
    isPanning.current = false;
    e.currentTarget.releasePointerCapture(e.pointerId);
  };

  const sIdx = Math.max(0, Math.floor(bounds.start));
  const eIdx = Math.min(dataLength, Math.ceil(bounds.end) + 1);

  return (
    <div
      ref={containerRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      style={{
        width: "100%",
        height: "100%",
        cursor: isPanning.current ? "grabbing" : "grab",
        touchAction: "none",
        overflow: "hidden"
      }}
    >
      {children(sIdx, eIdx)}
    </div>
  );
};

const KPI_CARDS_STORAGE_KEY = "excavator_kpi_cards_config_v1";

const DEFAULT_KPI_CARDS = [
  { id: "useful_time", title: "Полезное время", visible: true },
  { id: "volume", title: "Объем (В АВТО / Отвал)", visible: true },
  { id: "productivity_m3h", title: "Производительность (м³/ч)", visible: true },
  { id: "mass", title: "Масса породы", visible: true },
  { id: "cycles", title: "Всего циклов", visible: true },
  { id: "truck_arrivals", title: "Всего подъездов АС", visible: true },
  { id: "avg_loading_time", title: "Ср. время загрузки АС", visible: true },
  { id: "mileage", title: "Пробег по GPS", visible: true },
  { id: "oee", title: "Эффективность (OEE)", visible: true },
  // { id: "virtual_fuel", title: "Расход топлива (AI)", visible: true },
];

const AnalyticsDashboard = ({
  t,
  currentLang,
  analyticsData,
  classIdToMeta,
  pieData,
  formatDuration,
  actualTruckTotals,
  openSessionsModal,
  device,
  timelineComponent,
  volumeMode,
  onTimeJump
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const [showExtendedCharts, setShowExtendedCharts] = useState(false);
  const isSolid = volumeMode === "solid";
  const isTwin = volumeMode === "twin";
  const isTwinSolid = volumeMode === "twin_solid";
  const [showForecast, setShowForecast] = useState(false);

  // === СТЕЙТ И ЛОГИКА НАСТРОЙКИ ПЛАШЕК KPI (ХРАНЕНИЕ В БД НАВСЕГДА) ===
  const [kpiConfigModalVisible, setKpiConfigModalVisible] = useState(false);
  const [draggedCardId, setDraggedCardId] = useState(null);
  const [dragOverCardId, setDragOverCardId] = useState(null);
  const [savingKpiLayout, setSavingKpiLayout] = useState(false);

  // Вспомогательная функция валидации и слияния списка плашек
  const normalizeKpiConfig = (rawList) => {
    if (!Array.isArray(rawList)) return DEFAULT_KPI_CARDS;
    const merged = rawList
      .filter((item) => DEFAULT_KPI_CARDS.some((d) => d.id === item.id))
      .map((item) => {
        const def = DEFAULT_KPI_CARDS.find((d) => d.id === item.id);
        return { ...def, visible: Boolean(item.visible) };
      });
    DEFAULT_KPI_CARDS.forEach((def) => {
      if (!merged.some((m) => m.id === def.id)) {
        merged.push(def);
      }
    });
    return merged;
  };

  const [kpiCardsConfig, setKpiCardsConfig] = useState(() => {
    try {
      const saved = localStorage.getItem(KPI_CARDS_STORAGE_KEY);
      if (saved) return normalizeKpiConfig(JSON.parse(saved));
    } catch (e) {
      console.warn("Ошибка чтения локального кэша плашек KPI", e);
    }
    return DEFAULT_KPI_CARDS;
  });

  // Загружаем сохраненную конфигурацию пользователя с сервера при открытии дашборда
  useEffect(() => {
    let cancelled = false;
    const fetchUserKpiLayout = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;
        const res = await axios.get("/api/user/layout/", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled && res.data?.excavator_kpi_cards) {
          const normalized = normalizeKpiConfig(res.data.excavator_kpi_cards);
          setKpiCardsConfig(normalized);
          localStorage.setItem(KPI_CARDS_STORAGE_KEY, JSON.stringify(normalized));
        }
      } catch (e) {
        console.warn("Не удалось загрузить пользовательскую раскладку плашек с сервера", e);
      }
    };
    fetchUserKpiLayout();
    return () => {
      cancelled = true;
    };
  }, []);

  // Сохранение и в localStorage, и в БД (в профиль пользователя)
  const saveKpiConfig = async (newConfig) => {
    setKpiCardsConfig(newConfig);
    try {
      localStorage.setItem(KPI_CARDS_STORAGE_KEY, JSON.stringify(newConfig));
    } catch (e) {
      /* ignore */
    }

    setSavingKpiLayout(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        "/api/user/layout/",
        { excavator_kpi_cards: newConfig },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (e) {
      toast.error("Не удалось сохранить раскладку плашек на сервере");
    } finally {
      setSavingKpiLayout(false);
    }
  };

  const toggleKpiCardVisibility = (id) => {
    const next = kpiCardsConfig.map((c) =>
      c.id === id ? { ...c, visible: !c.visible } : c
    );
    saveKpiConfig(next);
  };

  const moveKpiCardStep = (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= kpiCardsConfig.length) return;
    const next = [...kpiCardsConfig];
    const [moved] = next.splice(index, 1);
    next.splice(targetIndex, 0, moved);
    saveKpiConfig(next);
  };

  const handleCardDrop = (targetId) => {
    if (!draggedCardId || draggedCardId === targetId) {
      setDraggedCardId(null);
      setDragOverCardId(null);
      return;
    }
    const next = [...kpiCardsConfig];
    const fromIdx = next.findIndex((c) => c.id === draggedCardId);
    const toIdx = next.findIndex((c) => c.id === targetId);
    if (fromIdx !== -1 && toIdx !== -1) {
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      saveKpiConfig(next);
    }
    setDraggedCardId(null);
    setDragOverCardId(null);
  };

  const resetKpiCardsToDefault = () => {
    saveKpiConfig(DEFAULT_KPI_CARDS);
    toast.info("Расположение плашек сброшено по умолчанию");
  };

  // Коэффициенты
  const globalSolidRatio = useMemo(() => {
    const est = analyticsData?.estimated_volume_total || 0;
    const sol = analyticsData?.solid_volume_total || 0;
    return (est > 0 && sol > 0) ? (sol / est) : 1.0;
  }, [analyticsData]);

  const aiVolRatio = useMemo(() => {
    const est = analyticsData?.estimated_volume_total || 0;
    const ai = analyticsData?.ai_volume_total || 0;
    return (est > 0 && ai > 0) ? (ai / est) : 1.0;
  }, [analyticsData]);

  const aiMassRatio = useMemo(() => {
    const est = analyticsData?.estimated_mass_total || 0;
    const ai = analyticsData?.ai_mass_total || 0;
    return (est > 0 && ai > 0) ? (ai / est) : 1.0;
  }, [analyticsData]);

  const [downtimeModal, setDowntimeModal] = useState({ visible: false, data: null });
  const [targetBuckets, setTargetBuckets] = useState(device?.target_buckets || 5);
  const saveTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (device && device.target_buckets !== undefined) {
      setTargetBuckets(device.target_buckets);
    }
  }, [device]);

  const saveTargetBuckets = async (val) => {
    if (!device?.id) return;
    const isStaff = localStorage.getItem("is_staff") === "true";
    if (!isStaff) return;

    try {
      const token = localStorage.getItem("token");
      await axios.patch(`/api/devices/${device.id}/`, {
        target_buckets: parseFloat(val) || 0
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("План ковшей сохранен");
    } catch (error) {
      toast.error("Ошибка при сохранении плана");
    }
  };

  const [ukvSettings, setUkvSettings] = useState({
    showBars: true,
    showLine: true,
    ukvMin: "",
    ukvMax: "",
    sessionMin: "",
    sessionMax: "",
  });

  const ukvScrollRef = React.useRef(null);
  const highlightTimeoutRef = React.useRef(null);
  const [highlightedUkvIndex, setHighlightedUkvIndex] = useState(null);

  // === РАСЧЕТ АНОМАЛИЙ УКВ ===
  const ukvData = useMemo(() => {
    const raw = analyticsData?.ukv_data;
    if (!raw || !raw.data || raw.data.length === 0) return null;

    const autoAvgUkv = raw.auto_avg_ukv || 0;
    const autoAvgSession = raw.auto_avg_session || 0;

    const bounds = {
      uMin: ukvSettings.ukvMin ? parseFloat(ukvSettings.ukvMin) : autoAvgUkv * 0.7,
      uMax: ukvSettings.ukvMax ? parseFloat(ukvSettings.ukvMax) : autoAvgUkv * 1.3,
      sMin: ukvSettings.sessionMin ? parseFloat(ukvSettings.sessionMin) : autoAvgSession * 0.7,
      sMax: ukvSettings.sessionMax ? parseFloat(ukvSettings.sessionMax) : autoAvgSession * 1.3,
    };
    const processedData = raw.data.map(d => {
      const reasons = [];
      if (d.ukv < bounds.uMin) reasons.push(`Быстрый цикл (<${bounds.uMin.toFixed(0)}с)`);
      if (d.ukv > bounds.uMax) reasons.push(`Долгий цикл (>${bounds.uMax.toFixed(0)}с)`);
      if (d.durationSec < bounds.sMin) reasons.push(`Быстрый заезд (<${bounds.sMin.toFixed(0)}с)`);
      if (d.durationSec > bounds.sMax) reasons.push(`Долгий заезд (>${bounds.sMax.toFixed(0)}с)`);

      return {
        ...d,
        isAnomaly: reasons.length > 0,
        anomalyReasons: reasons
      };
    });

    return {
      data: processedData,
      bounds,
      autoAvgUkv,
      autoAvgSession,
      anomalies: processedData.filter(d => d.isAnomaly),
      isLongPeriod: raw.is_long_period
    };
  }, [analyticsData, ukvSettings]);

  const scrollToAnomaly = (index) => {
    setHighlightedUkvIndex(index);
    if (ukvScrollRef.current && ukvData && ukvData.data) {
      const container = ukvScrollRef.current;
      const scrollWidth = container.scrollWidth;
      const clientWidth = container.clientWidth;
      const itemsCount = ukvData.data.length;
      const plotWidth = scrollWidth - 70;
      const barCenter = 50 + (plotWidth / itemsCount) * index + (plotWidth / itemsCount / 2);
      const scrollPos = barCenter - (clientWidth / 2);
      container.scrollTo({ left: Math.max(0, scrollPos), behavior: 'smooth' });
    }
    if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
    highlightTimeoutRef.current = setTimeout(() => { setHighlightedUkvIndex(null); }, 3000);
  };

  const ukvChartHeight = 350;
  const ukvMarginTop = 30;
  const ukvMarginBottom = 70;
  const ukvInnerHeight = ukvChartHeight - ukvMarginTop - ukvMarginBottom;

  const ukvMaxUkv = ukvData && ukvData.data.length > 0 ? Math.max(1, ...ukvData.data.map(d => d.ukv)) * 1.2 : 1;
  const ukvMaxSessionMin = ukvData && ukvData.data.length > 0 ? Math.max(1, ...ukvData.data.map(d => d.durationMin)) * 1.1 : 1;

  const getUkvY = (val) => {
    const y = ukvMarginTop + ukvInnerHeight - (val / ukvMaxUkv) * ukvInnerHeight;
    return Math.max(ukvMarginTop, Math.min(ukvMarginTop + ukvInnerHeight, y));
  };

  const getSessionY = (val) => {
    const y = ukvMarginTop + ukvInnerHeight - (val / ukvMaxSessionMin) * ukvInnerHeight;
    return Math.max(ukvMarginTop, Math.min(ukvMarginTop + ukvInnerHeight, y));
  };

  const CustomUkvLayer = ({ bars, innerHeight, width }) => {
    if (!ukvData) return null;

    const uMinY = innerHeight - (ukvData.bounds.uMin / ukvMaxUkv) * innerHeight;
    const uMaxY = innerHeight - (ukvData.bounds.uMax / ukvMaxUkv) * innerHeight;
    const sMinY = innerHeight - ((ukvData.bounds.sMin / 60) / ukvMaxSessionMin) * innerHeight;
    const sMaxY = innerHeight - ((ukvData.bounds.sMax / 60) / ukvMaxSessionMin) * innerHeight;

    const points = bars.map(bar => {
      const d = bar.data.data;
      const x = bar.x + bar.width / 2;
      const y = innerHeight - (d.ukv / ukvMaxUkv) * innerHeight;
      return { x, y, d };
    });

    const linePath = points.map((p, i) => (i === 0 ? `M ${p.x},${p.y}` : `L ${p.x},${p.y}`)).join(" ");

    return (
      <g>
        {ukvSettings.showBars && (
          <>
            <line x1={0} x2={width} y1={sMinY} y2={sMinY} stroke="#2eb85c" strokeWidth={1} strokeDasharray="4 4" opacity={0.8} />
            <line x1={0} x2={width} y1={sMaxY} y2={sMaxY} stroke="#e55353" strokeWidth={1} strokeDasharray="4 4" opacity={0.8} />
          </>
        )}

        {ukvSettings.showLine && (
          <>
            <line x1={0} x2={width} y1={uMinY} y2={uMinY} stroke="#2eb85c" strokeWidth={1} strokeDasharray="4 4" opacity={0.8} />
            <line x1={0} x2={width} y1={uMaxY} y2={uMaxY} stroke="#e55353" strokeWidth={1} strokeDasharray="4 4" opacity={0.8} />

            <path d={linePath} fill="none" stroke="#f9b115" strokeWidth={3} />

            {points.map((p, i) => (
              <g key={`ukv-pt-${i}`}>
                <circle cx={p.x} cy={p.y} r={p.d.isAnomaly ? 6 : 4} fill={p.d.isAnomaly ? "#e55353" : "#f9b115"} stroke="#fff" strokeWidth={2} />
                <text x={p.x} y={p.y - 12} textAnchor="middle" fontSize="11" fill={p.d.isAnomaly ? "#e55353" : "#495057"} fontWeight="bold" style={{ textShadow: "1px 1px 2px #fff, -1px -1px 2px #fff, 1px -1px 2px #fff, -1px 1px 2px #fff" }}>
                  {p.d.ukv}с
                </text>
              </g>
            ))}
          </>
        )}
      </g>
    );
  };

  const overlayLabelStyle = {
    position: "absolute",
    background: "rgba(255, 255, 255, 0.95)",
    padding: "2px 8px",
    borderRadius: "4px",
    fontSize: "11px",
    fontWeight: "bold",
    boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
    backdropFilter: "blur(3px)",
    transform: "translateY(-50%)",
    zIndex: 10,
    pointerEvents: "none"
  };

  const hintStyle = {
    borderBottom: "1px dashed #999",
    cursor: "help",
    width: "fit-content",
  };

  const chartDataWithForecast = useMemo(() => {
    const rawData = analyticsData?.productivity_chart?.data;
    if (!rawData || rawData.length === 0) return [];

    const activeData = rawData.filter(d => {
      let v = d.volume_total;
      if (isTwinSolid || isSolid) v = d.solid_volume_total;
      return v > 0 && !d.is_break;
    });

    const avgVeh = activeData.length ? activeData.reduce((acc, d) => {
      let v = d.volume_vehicle;
      if (isTwinSolid) v = d.solid_volume_vehicle * aiVolRatio;
      else if (isTwin) v = d.volume_vehicle * aiVolRatio;
      else if (isSolid) v = d.solid_volume_vehicle;
      return acc + v;
    }, 0) / activeData.length : 0;

    const avgGnd = activeData.length ? activeData.reduce((acc, d) => {
      let v = d.volume_ground;
      if (isTwinSolid) v = d.solid_volume_ground * aiVolRatio;
      else if (isTwin) v = d.volume_ground * aiVolRatio;
      else if (isSolid) v = d.solid_volume_ground;
      return acc + v;
    }, 0) / activeData.length : 0;

    const avgTrucks = activeData.length ? Math.round(activeData.reduce((acc, d) => acc + d.trucks, 0) / activeData.length) : 0;

    return rawData.map(d => {
      if (d.volume_total === 0 && !d.is_break && (d.downtime_sec || 0) < 1800) {
        return {
          ...d,
          is_forecast: true,
          forecast_volume_vehicle: Number(avgVeh.toFixed(1)),
          forecast_volume_ground: Number(avgGnd.toFixed(1)),
          forecast_trucks: avgTrucks,
          volume_vehicle: 0,
          volume_ground: 0,
        };
      }
      return {
        ...d,
        is_forecast: false,
        forecast_volume_vehicle: 0,
        forecast_volume_ground: 0,
        forecast_trucks: 0
      };
    });
  }, [analyticsData, isSolid, isTwin, isTwinSolid, aiVolRatio]);

  const CustomLineLayer = ({ xScale, innerHeight }) => {
    const data = chartDataWithForecast;
    const maxTrucks = Math.max(
      1,
      ...data.map((d) => Math.max(Number(d.trucks) || 0, Number(d.forecast_trucks) || 0))
    );

    const points = [];
    data.forEach((d) => {
      const xVal = xScale(d.time);
      if (xVal !== undefined) {
        const x = xVal + xScale.bandwidth() / 2;
        const isForecast = d.is_forecast;
        if (isForecast && !showForecast) return;
        const val = isForecast ? (Number(d.forecast_trucks) || 0) : (Number(d.trucks) || 0);

        if (val > 0 || isForecast) {
          const y = innerHeight - (val / maxTrucks) * (innerHeight * 0.85);
          points.push({ x, y, val, isForecast });
        }
      }
    });

    if (points.length === 0) return null;

    const linePath = points.map((p, i) => (i === 0 ? `M ${p.x},${p.y}` : `L ${p.x},${p.y}`)).join(" ");

    return (
      <g>
        <path d={linePath} fill="none" stroke="#0d6efd" strokeWidth={3} />

        {points.map((p, i) => (
          <g key={`truck-line-${i}`}>
            <circle
              cx={p.x} cy={p.y} r={5}
              fill={p.isForecast ? "#fff" : "#0d6efd"}
              stroke="#0d6efd"
              strokeWidth={p.isForecast ? 1.5 : 2}
              strokeDasharray={p.isForecast ? "2 2" : "none"}
            />
            <text
              x={p.x} y={p.y - 12}
              textAnchor="middle" fill="#0d6efd" fontSize="11"
              fontWeight={p.isForecast ? "normal" : "bold"}
              style={{ textShadow: "1px 1px 2px #fff, -1px -1px 2px #fff, 1px -1px 2px #fff, -1px 1px 2px #fff" }}
            >
              {p.val} {p.isForecast && "шт?"}
            </text>
          </g>
        ))}
      </g>
    );
  };

  const sCurveMapped = useMemo(() => {
    const rawData = analyticsData?.productivity_chart?.data;
    if (!rawData) return [];

    let runFact = 0;
    let runPlan = 0;
    const planStep = (analyticsData.kpis?.vNorm || 0) / rawData.length;

    const factData = [];
    const planData = [];

    rawData.forEach(d => {
      let vol = d.volume_total;
      if (isTwinSolid) {
        let sr = d.volume_total > 0 ? d.solid_volume_total / d.volume_total : globalSolidRatio;
        vol = (d.volume_total * aiVolRatio) * sr;
      } else if (isTwin) {
        vol = d.volume_total * aiVolRatio;
      } else if (isSolid) {
        vol = d.solid_volume_total;
      }

      runFact += vol;
      runPlan += planStep;

      factData.push({ x: d.time, y: Number(runFact.toFixed(1)) });
      planData.push({ x: d.time, y: Number(runPlan.toFixed(1)) });
    });

    return [
      { id: "Норма", color: "hsl(0, 0%, 50%)", data: planData },
      { id: "Факт", color: "hsl(145, 70%, 50%)", data: factData }
    ];
  }, [analyticsData, isSolid, isTwin, isTwinSolid, aiVolRatio, globalSolidRatio]);

  // =========================================================================
  // ПРОВЕРКИ НАЛИЧИЯ ДАННЫХ ДЛЯ СКРЫТИЯ ПУСТЫХ ГРАФИКОВ
  // =========================================================================
  const hasProductivityData = useMemo(() => {
    const data = analyticsData?.productivity_chart?.data;
    if (!data || data.length === 0) return false;
    return data.some(d => 
      (Number(d.volume_total) || 0) > 0 || 
      (Number(d.solid_volume_total) || 0) > 0 ||
      (Number(d.trucks) || 0) > 0 || 
      (Number(d.cycles_vehicle) || 0) > 0 || 
      (Number(d.cycles_ground) || 0) > 0
    );
  }, [analyticsData]);

  const hasSCurveData = useMemo(() => {
    if (!sCurveMapped || sCurveMapped.length === 0) return false;
    const factMax = Math.max(0, ...(sCurveMapped.find(s => s.id === "Факт")?.data.map(d => Number(d.y) || 0) || [0]));
    const normMax = Math.max(0, ...(sCurveMapped.find(s => s.id === "Норма")?.data.map(d => Number(d.y) || 0) || [0]));
    return factMax > 0 || normMax > 0;
  }, [sCurveMapped]);

  const hasTimeBalanceData = useMemo(() => {
    const tWork = analyticsData?.kpis?.tWork || 0;
    const durs = analyticsData?.durations_by_class_sec || {};
    const totalDowntime = (durs.actual_downtime || 0) + (durs.no_data || 0);
    return tWork > 0 || totalDowntime > 0;
  }, [analyticsData]);

  const hasCycleComponentsData = useMemo(() => {
    const items = analyticsData?.extra_dash_charts?.cycle_components;
    if (!items || items.length === 0) return false;
    return items.some(item => 
      (Number(item['Возврат и копание']) || 0) > 0 ||
      (Number(item['Поворот на разгрузку']) || 0) > 0 ||
      (Number(item['Процесс разгрузки']) || 0) > 0 ||
      (Number(item['Микро-простои']) || 0) > 0
    );
  }, [analyticsData]);

  const hasBucketsPerTruckData = useMemo(() => {
    const bucketsData = analyticsData?.extra_dash_charts?.buckets_per_truck;
    if (!bucketsData || bucketsData.length === 0) return false;
    return bucketsData.some(d => d.avg_buckets !== null && d.avg_buckets !== undefined && Number(d.avg_buckets) > 0);
  }, [analyticsData]);

  const hasParetoData = useMemo(() => {
    const paretoData = analyticsData?.extra_dash_charts?.pareto_data;
    if (!paretoData || paretoData.length === 0) return false;
    return paretoData.some(d => (Number(d.frequency) || 0) > 0);
  }, [analyticsData]);

  const hasUkvData = useMemo(() => {
    return Boolean(
      ukvData &&
      ukvData.data &&
      ukvData.data.length > 0 &&
      ukvData.data.some(d => (Number(d.durationSec) || 0) > 0 || (Number(d.ukv) || 0) > 0)
    );
  }, [ukvData]);

  const hasAnyExtendedCharts = useMemo(() => {
    return (
      hasProductivityData ||
      hasSCurveData ||
      hasTimeBalanceData ||
      hasCycleComponentsData ||
      hasBucketsPerTruckData ||
      hasParetoData ||
      hasUkvData
    );
  }, [
    hasProductivityData,
    hasSCurveData,
    hasTimeBalanceData,
    hasCycleComponentsData,
    hasBucketsPerTruckData,
    hasParetoData,
    hasUkvData
  ]);

  return (
    <div id="analytics-content" style={{ padding: "5px", background: "white" }}>

      {/* --- КАРТОЧКИ С ОСНОВНЫМИ ПОКАЗАТЕЛЯМИ (KPI) --- */}
      <div className="mb-4 mt-2">
        <div className="d-flex justify-content-between align-items-center mb-2 px-1">
          <span className="text-muted small">
            💡 <i>Плашки можно перетаскивать мышью или настроить через кнопку справа</i>
          </span>
          <CButton
            color="light"
            size="sm"
            className="border shadow-sm fw-semibold d-flex align-items-center gap-1 text-dark"
            onClick={() => setKpiConfigModalVisible(true)}
          >
            <CIcon icon={cilSettings} size="sm" className="text-primary" />
            Настроить плашки
          </CButton>
        </div>

        <CRow className="g-3">
          {kpiCardsConfig
            .filter((c) => {
              if (!c.visible) return false;
              if (
                (c.id === "truck_arrivals" || c.id === "avg_loading_time") &&
                device?.hide_truck_stats
              ) {
                return false;
              }
              if (c.id === "virtual_fuel" && !analyticsData.virtual_fuel) {
                return false;
              }
              return true;
            })
            .map((cardMeta) => {
              const isOver = dragOverCardId === cardMeta.id && draggedCardId !== cardMeta.id;

              const renderCardContent = () => {
                switch (cardMeta.id) {
                  case "useful_time": {
                    const useful_mh = analyticsData.kpis?.useful_work_mh || 0;
                    const scheduled_sec = analyticsData.total_duration_sec || 0;
                    const no_data_sec = analyticsData.durations_by_class_sec?.no_data || 0;
                    const uptime_mh = Math.max(0, scheduled_sec - no_data_sec) / 3600.0;
                    const percent = uptime_mh > 0 ? (useful_mh / uptime_mh) * 100 : 0;
                    let pctColor = "text-success";
                    if (percent < 30) pctColor = "text-danger";
                    else if (percent < 60) pctColor = "text-warning";

                    return (
                      <CCardBody className="text-center d-flex flex-column p-3">
                        <div className="text-muted small text-uppercase fw-bold mb-2 text-truncate" style={{ fontSize: "0.75rem", height: "18px" }}>
                          {t("analysis.useful_time", "Полезное время")}
                        </div>
                        <div className="flex-grow-1 d-flex align-items-center justify-content-center my-1 text-nowrap">
                          <span className="fw-bold text-primary" style={{ fontSize: "1.6rem", lineHeight: 1 }}>
                            {Number(useful_mh).toFixed(1)}
                          </span>
                          <span className="text-muted fw-bold mx-1" style={{ fontSize: "1.6rem", lineHeight: 1 }}>
                            /
                          </span>
                          <span className="fw-bold text-muted" style={{ fontSize: "1.6rem", lineHeight: 1 }}>
                            {Number(uptime_mh).toFixed(1)}
                          </span>
                          <span className="text-muted fw-semibold ms-1" style={{ fontSize: "0.9rem" }}>
                            {t("analysis.machine_hours", "маш.-ч")}
                          </span>
                        </div>
                        <div className="mt-2 d-flex align-items-center justify-content-center" style={{ height: "22px" }}>
                          <span className={`${pctColor} fw-bold`} style={{ fontSize: "0.9rem" }}>
                            {Number(percent).toFixed(1)}%
                          </span>
                        </div>
                      </CCardBody>
                    );
                  }

                  case "volume": {
                    const volTotal = Number(
                      isTwinSolid
                        ? (analyticsData.ai_volume_total || 0) * globalSolidRatio
                        : isTwin
                          ? (analyticsData.ai_volume_total || 0)
                          : isSolid
                            ? (analyticsData.solid_volume_total || 0)
                            : (analyticsData.estimated_volume_total || 0)
                    );
                    const volVehicle = Number(
                      isTwinSolid
                        ? (analyticsData.ai_volume_vehicle || 0) * globalSolidRatio
                        : isTwin
                          ? (analyticsData.ai_volume_vehicle || 0)
                          : isSolid
                            ? (analyticsData.solid_volume_vehicle || 0)
                            : (analyticsData.estimated_volume_vehicle || 0)
                    );
                    const volGround = Number(
                      isTwinSolid
                        ? (analyticsData.ai_volume_ground || 0) * globalSolidRatio
                        : isTwin
                          ? (analyticsData.ai_volume_ground || 0)
                          : isSolid
                            ? (analyticsData.solid_volume_ground || 0)
                            : (analyticsData.estimated_volume_ground || 0)
                    );

                    return (
                      <CCardBody className="text-center d-flex flex-column p-3">
                        <div className="text-muted small text-uppercase fw-bold mb-2">
                          {isTwinSolid
                            ? "AI Объем (тв. тело)"
                            : isTwin
                              ? "AI Объем двойника"
                              : isSolid
                                ? "Объем в твердом теле"
                                : "Номинальный объем"}
                        </div>
                        <div className="flex-grow-1 d-flex flex-column align-items-center justify-content-center my-1">
                          <div className="fs-3 fw-bold text-success lh-1">
                            В АВТО: {volVehicle.toFixed(1)} <span className="fs-6">м³</span>
                          </div>
                        </div>
                        <div className="mt-2 d-flex align-items-center justify-content-center" style={{ minHeight: "20px" }}>
                          <div className="d-flex justify-content-center gap-3 small fw-bold m-0">
                            <span className="text-warning" title="В отвал (землю)">
                              Отвал: {volGround.toFixed(1)} м³
                            </span>
                            <span className="text-muted" title="Суммарный объем">
                              Всего: {volTotal.toFixed(1)} м³
                            </span>
                          </div>
                        </div>
                      </CCardBody>
                    );
                  }

                  case "productivity_m3h": {
                    const usefulMh = Number(analyticsData.kpis?.useful_work_mh || 0);
                    const volTotal = Number(
                      isTwinSolid
                        ? (analyticsData.ai_volume_total || 0) * globalSolidRatio
                        : isTwin
                          ? (analyticsData.ai_volume_total || 0)
                          : isSolid
                            ? (analyticsData.solid_volume_total || 0)
                            : (analyticsData.estimated_volume_total || 0)
                    );
                    const volVehicle = Number(
                      isTwinSolid
                        ? (analyticsData.ai_volume_vehicle || 0) * globalSolidRatio
                        : isTwin
                          ? (analyticsData.ai_volume_vehicle || 0)
                          : isSolid
                            ? (analyticsData.solid_volume_vehicle || 0)
                            : (analyticsData.estimated_volume_vehicle || 0)
                    );
                    const volGround = Number(
                      isTwinSolid
                        ? (analyticsData.ai_volume_ground || 0) * globalSolidRatio
                        : isTwin
                          ? (analyticsData.ai_volume_ground || 0)
                          : isSolid
                            ? (analyticsData.solid_volume_ground || 0)
                            : (analyticsData.estimated_volume_ground || 0)
                    );

                    const rateTotal = usefulMh > 0 ? volTotal / usefulMh : 0;
                    const rateVehicle = usefulMh > 0 ? volVehicle / usefulMh : 0;
                    const rateGround = usefulMh > 0 ? volGround / usefulMh : 0;

                    return (
                      <CCardBody className="text-center d-flex flex-column p-3">
                        <SafeTooltip content="Выработка в кубометрах за 1 час чистой работы экскаватора (без обедов и простоев)">
                          <div className="text-muted small text-uppercase fw-bold mb-2 mx-auto" style={hintStyle}>
                            Производительность
                          </div>
                        </SafeTooltip>
                        <div className="flex-grow-1 d-flex align-items-center justify-content-center">
                          <div className="fs-3 fw-bold text-primary lh-1">
                            {rateTotal.toFixed(1)} <span className="fs-6">м³/ч</span>
                          </div>
                        </div>
                        <div className="mt-2 d-flex align-items-center justify-content-center" style={{ minHeight: "20px" }}>
                          {rateTotal > 0 && (
                            <div className="d-flex justify-content-center gap-3 small fw-bold m-0">
                              <span className="text-success" title="В авто за чистый час работы">
                                В АВТО: {rateVehicle.toFixed(1)}
                              </span>
                              <span className="text-warning" title="В отвал за чистый час работы">
                                Отвал: {rateGround.toFixed(1)}
                              </span>
                            </div>
                          )}
                        </div>
                      </CCardBody>
                    );
                  }

                  case "mass": {
                    return (
                      <CCardBody className="text-center d-flex flex-column p-3">
                        <div className="text-muted small text-uppercase fw-bold mb-2">
                          {isTwin || isTwinSolid ? "AI Масса двойника" : t("analysis.mass_nom", "Номинальная масса")}
                        </div>
                        <div className="flex-grow-1 d-flex align-items-center justify-content-center">
                          <div className="fs-3 fw-bold text-dark lh-1">
                            {(
                              Number(
                                isTwin || isTwinSolid
                                  ? analyticsData.ai_mass_total || 0
                                  : analyticsData.estimated_mass_total || 0
                              ) / 1000
                            ).toFixed(1)}{" "}
                            <span className="fs-6">т</span>
                          </div>
                        </div>
                        <div className="mt-2" style={{ minHeight: "20px" }}></div>
                      </CCardBody>
                    );
                  }

                  case "cycles": {
                    return (
                      <CCardBody className="text-center d-flex flex-column p-3">
                        <div className="text-muted small text-uppercase fw-bold mb-2">
                          {t("analysis.total_cycles", "Всего циклов")}
                        </div>
                        <div className="flex-grow-1 d-flex align-items-center justify-content-center">
                          <div className="fs-3 fw-bold text-dark lh-1">
                            {analyticsData.cycle_count_total || 0}
                          </div>
                        </div>
                        <div className="mt-2 d-flex align-items-center justify-content-center" style={{ minHeight: "20px" }}>
                          {(analyticsData.cycle_count_total || 0) > 0 && (
                            <div className="d-flex justify-content-center gap-3 small fw-bold m-0">
                              <span className="text-success" title="В самосвал">
                                В АВТО: {analyticsData.cycle_count_vehicle || 0}
                              </span>
                              <span className="text-warning" title="В отвал (землю)">
                                Отвал: {analyticsData.cycle_count_ground || 0}
                              </span>
                            </div>
                          )}
                        </div>
                      </CCardBody>
                    );
                  }

                  case "truck_arrivals": {
                    const totalArrivals = analyticsData.truck_arrivals_total || 0;
                    const unidentifiedArrivals =
                      analyticsData.truck_stats_summary?.["Неизвестная машина"]?.arrivals || 0;
                    const identifiedArrivals = Math.max(0, totalArrivals - unidentifiedArrivals);
                    const identifiedPct = totalArrivals > 0 ? (identifiedArrivals / totalArrivals) * 100 : 0;
                    const unidentifiedPct = totalArrivals > 0 ? (unidentifiedArrivals / totalArrivals) * 100 : 0;

                    return (
                      <CCardBody className="text-center d-flex flex-column p-3">
                        <div className="text-muted small text-uppercase fw-bold mb-2">
                          {t("analysis.truck_arrivals_total", "Всего подъездов")}
                        </div>
                        <div className="flex-grow-1 d-flex align-items-center justify-content-center">
                          <div className="fs-3 fw-bold text-primary lh-1">{totalArrivals}</div>
                        </div>
                        <div className="mt-2 w-100 d-flex align-items-center justify-content-center" style={{ minHeight: "20px" }}>
                          {totalArrivals > 0 && (
                            <div
                              className="w-100 d-flex rounded overflow-hidden shadow-sm m-0"
                              style={{ height: "8px", backgroundColor: "#e9ecef" }}
                            >
                              {identifiedPct > 0 && (
                                <SafeTooltip placement="top" content={`Идентифицированы: ${identifiedArrivals} шт.`}>
                                  <div
                                    style={{
                                      width: `${identifiedPct}%`,
                                      backgroundColor: "#2eb85c",
                                      cursor: "help",
                                    }}
                                  />
                                </SafeTooltip>
                              )}
                              {unidentifiedPct > 0 && (
                                <SafeTooltip placement="top" content={`Неизвестные: ${unidentifiedArrivals} шт.`}>
                                  <div
                                    style={{
                                      width: `${unidentifiedPct}%`,
                                      backgroundColor: "#f9b115",
                                      cursor: "help",
                                    }}
                                  />
                                </SafeTooltip>
                              )}
                            </div>
                          )}
                        </div>
                      </CCardBody>
                    );
                  }

                  case "avg_loading_time": {
                    return (
                      <CCardBody className="text-center d-flex flex-column p-3">
                        <div className="text-muted small text-uppercase fw-bold mb-2">
                          {t("analysis.avg_loading_session", "Ср. время загрузки")}
                        </div>
                        <div className="flex-grow-1 d-flex align-items-center justify-content-center">
                          <div className="fs-3 fw-bold text-success lh-1">
                            {formatDuration(analyticsData.averages.vehicle.loading_session_sec)}
                          </div>
                        </div>
                        <div className="mt-2" style={{ minHeight: "20px" }}></div>
                      </CCardBody>
                    );
                  }

                  case "mileage": {
                    return (
                      <CCardBody className="text-center d-flex flex-column p-3">
                        <div className="text-muted small text-uppercase fw-bold mb-2">
                          {t("analysis.total_mileage", "Пробег по GPS")}
                        </div>
                        <div className="flex-grow-1 d-flex align-items-center justify-content-center">
                          <div className="fs-3 fw-bold text-info lh-1">
                            {Number(analyticsData.total_mileage_km || 0).toFixed(2)}{" "}
                            <span className="fs-6">км</span>
                          </div>
                        </div>
                        <div className="mt-2" style={{ minHeight: "20px" }}></div>
                      </CCardBody>
                    );
                  }

                  case "oee": {
                    return (
                      <CCardBody className="text-center d-flex flex-column p-3">
                        <div className="text-muted small text-uppercase fw-bold mb-2">
                          Эффективность (OEE)
                        </div>
                        <div className="flex-grow-1 d-flex align-items-center justify-content-center">
                          <div className="fs-3 fw-bold text-success lh-1">
                            {Number(analyticsData.kpis?.OEE || 0).toFixed(1)}{" "}
                            <span className="fs-6">%</span>
                          </div>
                        </div>
                        <div className="mt-2" style={{ minHeight: "20px" }}></div>
                      </CCardBody>
                    );
                  }

                  case "virtual_fuel": {
                    return (
                      <CCardBody className="text-center d-flex flex-column p-3">
                        <div className="text-muted small text-uppercase fw-bold mb-2" title="Расчетный расход по моточасам">
                          Расход топлива (AI)
                        </div>
                        <div className="flex-grow-1 d-flex align-items-center justify-content-center">
                          <div className="fs-3 fw-bold text-danger lh-1">
                            {analyticsData.virtual_fuel.total_liters}{" "}
                            <span className="fs-6">л</span>
                          </div>
                        </div>
                        <div className="mt-2 d-flex align-items-center justify-content-center" style={{ minHeight: "20px" }}>
                          <SafeTooltip
                            content={
                              <div className="small text-start">
                                <div><strong>Спецификация:</strong> {analyticsData.virtual_fuel.spec_name}</div>
                                <div><strong>Коэфф. (K):</strong> {analyticsData.virtual_fuel.k_factor}</div>
                                <hr className="my-1 border-secondary" />
                                <div>Холостой: {analyticsData.virtual_fuel.liters_idle} л ({analyticsData.virtual_fuel.hours_idle} ч)</div>
                                <div>Средний: {analyticsData.virtual_fuel.liters_medium} л ({analyticsData.virtual_fuel.hours_medium} ч)</div>
                                <div>Высокий: {analyticsData.virtual_fuel.liters_high} л ({analyticsData.virtual_fuel.hours_high} ч)</div>
                              </div>
                            }
                          >
                            <div className="text-muted fw-bold text-truncate" style={{ fontSize: "0.75rem", cursor: "help", maxWidth: "100%" }}>
                              {analyticsData.virtual_fuel.spec_name} <CIcon icon={cilInfo} size="sm" />
                            </div>
                          </SafeTooltip>
                        </div>
                      </CCardBody>
                    );
                  }

                  default:
                    return null;
                }
              };

              return (
                <CCol
                  xs={6}
                  md={4}
                  xl={3}
                  key={cardMeta.id}
                  draggable
                  onDragStart={() => setDraggedCardId(cardMeta.id)}
                  onDragOver={(e) => {
                    e.preventDefault();
                    if (dragOverCardId !== cardMeta.id) {
                      setDragOverCardId(cardMeta.id);
                    }
                  }}
                  onDragLeave={() => {
                    if (dragOverCardId === cardMeta.id) {
                      setDragOverCardId(null);
                    }
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    handleCardDrop(cardMeta.id);
                  }}
                  onDragEnd={() => {
                    setDraggedCardId(null);
                    setDragOverCardId(null);
                  }}
                  style={{
                    cursor: "grab",
                    opacity: draggedCardId === cardMeta.id ? 0.45 : 1,
                    transform: isOver ? "scale(1.02)" : "none",
                    transition: "transform 0.15s ease, opacity 0.15s ease",
                  }}
                  title="Перетащите плашку, чтобы поменять местами"
                >
                  <CCard
                    className={`h-100 shadow-sm bg-light ${
                      isOver ? "border border-2 border-primary" : "border-0"
                    }`}
                  >
                    {renderCardContent()}
                  </CCard>
                </CCol>
              );
            })}
        </CRow>
      </div>

      {/* --- МОДАЛЬНОЕ ОКНО НАСТРОЙКИ И СОРТИРОВКИ ПЛАШЕК KPI --- */}
      <CModal
        visible={kpiConfigModalVisible}
        onClose={() => setKpiConfigModalVisible(false)}
        alignment="center"
      >
        <CModalHeader closeButton className="bg-light">
          <CModalTitle className="fw-bold fs-6 d-flex align-items-center gap-2">
            <CIcon icon={cilSettings} className="text-primary" />
            Настройка отображения и порядка плашек
          </CModalTitle>
        </CModalHeader>
        <CModalBody className="p-3">
          <div className="text-muted small mb-3">
            Включите нужные показатели и расположите их в удобном порядке (перетаскиванием или стрелками):
          </div>
          <CListGroup>
            {kpiCardsConfig.map((card, idx) => (
              <CListGroupItem
                key={card.id}
                draggable
                onDragStart={() => setDraggedCardId(card.id)}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOverCardId(card.id);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  handleCardDrop(card.id);
                }}
                onDragEnd={() => {
                  setDraggedCardId(null);
                  setDragOverCardId(null);
                }}
                className={`d-flex justify-content-between align-items-center py-2 px-3 ${
                  dragOverCardId === card.id ? "border-primary bg-light" : ""
                }`}
                style={{ cursor: "grab" }}
              >
                <div className="d-flex align-items-center gap-3">
                  <span className="text-muted fw-bold" style={{ width: "22px" }}>
                    {idx + 1}.
                  </span>
                  <CFormSwitch
                    size="lg"
                    id={`kpi-switch-${card.id}`}
                    checked={card.visible}
                    onChange={() => toggleKpiCardVisibility(card.id)}
                    style={{ cursor: "pointer" }}
                  />
                  <label
                    htmlFor={`kpi-switch-${card.id}`}
                    className={`mb-0 fw-semibold ${
                      card.visible ? "text-dark" : "text-muted text-decoration-line-through"
                    }`}
                    style={{ cursor: "pointer" }}
                  >
                    {card.title}
                  </label>
                </div>

                <div className="d-flex align-items-center gap-1">
                  <CButton
                    color="light"
                    size="sm"
                    className="border p-1 px-2"
                    disabled={idx === 0}
                    onClick={() => moveKpiCardStep(idx, -1)}
                    title="Поднять выше"
                  >
                    <CIcon icon={cilArrowTop} size="sm" />
                  </CButton>
                  <CButton
                    color="light"
                    size="sm"
                    className="border p-1 px-2"
                    disabled={idx === kpiCardsConfig.length - 1}
                    onClick={() => moveKpiCardStep(idx, 1)}
                    title="Опустить ниже"
                  >
                    <CIcon icon={cilArrowBottom} size="sm" />
                  </CButton>
                </div>
              </CListGroupItem>
            ))}
          </CListGroup>
        </CModalBody>
        <CModalFooter className="bg-light d-flex justify-content-between">
          <CButton
            color="secondary"
            variant="outline"
            size="sm"
            className="fw-semibold d-flex align-items-center gap-1"
            onClick={resetKpiCardsToDefault}
          >
            <CIcon icon={cilReload} size="sm" />
            Сбросить по умолчанию
          </CButton>
          <CButton
            color="primary"
            size="sm"
            className="fw-bold px-4"
            onClick={() => setKpiConfigModalVisible(false)}
          >
            Готово
          </CButton>
        </CModalFooter>
      </CModal>

      {/* --- КНОПКА РАСКРЫТИЯ / СВОРАЧИВАНИЯ ГРАФИКОВ --- */}
      <div className="text-center my-3 d-flex justify-content-center">
        <CButton
          color="primary"
          className="fw-bold shadow-sm d-flex align-items-center justify-content-center text-white"
          style={{ height: "38px", minWidth: "350px", borderRadius: "8px" }}
          onClick={() => setShowExtendedCharts(!showExtendedCharts)}
        >
          <CIcon icon={showExtendedCharts ? cilChevronTop : cilChartLine} className="me-2 text-white" />
          <span>
            {showExtendedCharts
              ? "Скрыть расширенную аналитику"
              : "Развернуть расширенную аналитику"}
          </span>
          <CIcon icon={showExtendedCharts ? cilChevronTop : cilChevronBottom} className="ms-2 text-white" />
        </CButton>
      </div>

      {/* --- РАЗВОРАЧИВАЕМЫЙ БЛОК ВСЕХ ГРАФИКОВ --- */}
      {showExtendedCharts && (
        <div className="mt-3">
          {!hasAnyExtendedCharts ? (
            <div className="p-4 text-center text-muted bg-light rounded border border-dashed mb-4">
              За выбранный период нет данных для построения графиков
            </div>
          ) : (
            <>
              <svg width="0" height="0">
                <defs>
                  <pattern id="diagonal-stripes" width="8" height="8" patternTransform="rotate(45)" patternUnits="userSpaceOnUse">
                    <rect width="8" height="8" fill="transparent" />
                    <line x1="0" y1="0" x2="0" y2="8" stroke="#dcdcdc" strokeWidth="4" />
                  </pattern>
                </defs>
              </svg>

              {/* 1. ГРАФИК ДИНАМИКИ РАБОТЫ */}
              {hasProductivityData && (
                <CRow className="mb-4 mt-2">
                  <CCol xs={12}>
                    <CCard className="shadow-sm border-0 h-100">
                      <CCardHeader className="bg-white fw-bold d-flex justify-content-between align-items-center">
                        <span>Динамика работы</span>
                        <div className="d-flex gap-2 flex-wrap align-items-center">
                          <CFormCheck
                            id="showForecastToggle"
                            label={<span className="small text-muted fw-bold">Упущенная выгода</span>}
                            checked={showForecast}
                            onChange={(e) => setShowForecast(e.target.checked)}
                            className="mb-0 me-3"
                            style={{ cursor: "pointer" }}
                          />
                          <CBadge color="success" shape="rounded-pill">В самосвал (м³)</CBadge>
                          <CBadge color="warning" shape="rounded-pill">В отвал (м³)</CBadge>
                          <CBadge color="primary" shape="rounded-pill">Машины (шт)</CBadge>
                        </div>
                      </CCardHeader>
                      <CCardBody className="p-0">
                        <div style={{ width: "100%", overflowX: "auto", overflowY: "hidden" }}>
                          <div style={{ minWidth: "100%", height: "420px", paddingTop: "10px" }}>
                            {(() => {
                              const barDataReadyFull = chartDataWithForecast.map(d => {
                                let v_veh = d.volume_vehicle;
                                let v_gnd = d.volume_ground;
                                let v_tot = d.volume_total;

                                if (isTwinSolid) {
                                  let sr = d.volume_total > 0 ? d.solid_volume_total / d.volume_total : globalSolidRatio;
                                  v_veh = (d.volume_vehicle * aiVolRatio) * sr;
                                  v_gnd = (d.volume_ground * aiVolRatio) * sr;
                                  v_tot = (d.volume_total * aiVolRatio) * sr;
                                } else if (isTwin) {
                                  v_veh = d.volume_vehicle * aiVolRatio;
                                  v_gnd = d.volume_ground * aiVolRatio;
                                  v_tot = d.volume_total * aiVolRatio;
                                } else if (isSolid) {
                                  v_veh = d.solid_volume_vehicle;
                                  v_gnd = d.solid_volume_ground;
                                  v_tot = d.solid_volume_total;
                                }

                                return {
                                  ...d,
                                  volume_vehicle: v_veh,
                                  volume_ground: v_gnd,
                                  volume_total: v_tot,
                                  forecast_volume_vehicle: showForecast ? d.forecast_volume_vehicle : 0,
                                  forecast_volume_ground: showForecast ? d.forecast_volume_ground : 0,
                                };
                              });

                              return (
                                <ZoomableNivoWrapper dataLength={barDataReadyFull.length} minWindow={5}>
                                  {(sIdx, eIdx) => {
                                    const slicedData = barDataReadyFull.slice(sIdx, eIdx);

                                    return (
                                      <ResponsiveBar
                                        data={slicedData}
                                        keys={["volume_vehicle", "volume_ground", "forecast_volume_vehicle", "forecast_volume_ground"]}
                                        indexBy="time"
                                        margin={{ top: 140, right: 30, bottom: 80, left: 60 }}
                                        padding={0.3}
                                        valueScale={{ type: "linear" }}
                                        indexScale={{ type: "band", round: true }}
                                        colors={({ id }) => {
                                          if (id === "volume_vehicle" || id === "forecast_volume_vehicle") return "#2eb85c";
                                          if (id === "volume_ground" || id === "forecast_volume_ground") return "#ffc107";
                                          return "#ccc";
                                        }}
                                        defs={[
                                          {
                                            id: 'pattern-green',
                                            type: 'patternLines',
                                            background: 'transparent',
                                            color: '#2eb85c',
                                            rotation: -45,
                                            lineWidth: 3,
                                            spacing: 6
                                          },
                                          {
                                            id: 'pattern-yellow',
                                            type: 'patternLines',
                                            background: 'transparent',
                                            color: '#ffc107',
                                            rotation: -45,
                                            lineWidth: 3,
                                            spacing: 6
                                          }
                                        ]}
                                        fill={[
                                          { match: { id: 'forecast_volume_vehicle' }, id: 'pattern-green' },
                                          { match: { id: 'forecast_volume_ground' }, id: 'pattern-yellow' }
                                        ]}
                                        borderRadius={2}
                                        axisTop={null}
                                        axisRight={null}
                                        axisBottom={{
                                          tickSize: 5,
                                          tickPadding: 5,
                                          tickRotation: -45,
                                          tickValues: slicedData.length > 12
                                            ? slicedData
                                              .filter((_, i) => i % Math.ceil(slicedData.length / 10) === 0)
                                              .map(d => d.time)
                                            : undefined
                                        }}
                                        axisLeft={{ tickSize: 5, tickPadding: 5, legend: "Объем (м³)", legendPosition: "middle", legendOffset: -50 }}
                                        labelSkipWidth={16}
                                        labelSkipHeight={16}
                                        labelTextColor="#ffffff"
                                        layers={[
                                          "grid",
                                          "axes",
                                          ({ xScale, innerHeight }) =>
                                            slicedData.filter((d) => d.is_break).map((d, i) => {
                                              const x = xScale(d.time);
                                              const w = xScale.bandwidth();
                                              if (x === undefined) return null;
                                              return (
                                                <g key={`break-${i}`}>
                                                  <rect x={x} y={0} width={w} height={innerHeight} fill="url(#diagonal-stripes)" />
                                                  <text
                                                    x={x + w / 2}
                                                    y={-5}
                                                    textAnchor="start"
                                                    transform={`rotate(-45 ${x + w / 2} -5)`}
                                                    fill="#6c757d"
                                                    fontSize="11"
                                                    fontWeight="bold"
                                                  >
                                                    {d.break_desc || "Перерыв"}
                                                  </text>
                                                </g>
                                              );
                                            }),
                                          "bars",
                                          CustomLineLayer,
                                          "markers",
                                        ]}
                                        markers={[
                                          {
                                            axis: "y", value: analyticsData.productivity_chart.avg_volume,
                                            lineStyle: { stroke: "#321fdb", strokeWidth: 2, strokeDasharray: "4 4" },
                                            legend: `Ср. факт: ${analyticsData.productivity_chart.avg_volume} м³`,
                                            legendOrientation: "horizontal", legendPosition: "top-right",
                                            textStyle: { fill: "#321fdb", fontSize: 12, fontWeight: "bold" },
                                          },
                                        ]}
                                        tooltip={({ data }) => {
                                          const row = data;

                                          if (row.is_forecast) {
                                            if (!showForecast) {
                                              return (
                                                <div style={{ padding: "10px", background: "white", border: "1px solid #ccc", borderRadius: "6px", boxShadow: "0 4px 6px rgba(0,0,0,0.1)", minWidth: "160px", zIndex: 999 }}>
                                                  <div className="fw-bold mb-1 border-bottom pb-1 text-center" style={{ fontSize: "0.85rem" }}>
                                                    {row.time} {row.is_break && <span className="text-muted fw-normal">(Перерыв)</span>}
                                                  </div>
                                                  <div className="text-muted text-center mt-1" style={{ fontSize: "0.75rem", lineHeight: 1.2 }}>
                                                    Нет данных о погрузке
                                                  </div>
                                                </div>
                                              );
                                            }
                                            const total_forecast = Number((row.forecast_volume_vehicle + row.forecast_volume_ground).toFixed(1));
                                            return (
                                              <div style={{
                                                padding: "8px",
                                                background: "white",
                                                border: "1px dashed #999",
                                                borderRadius: "4px",
                                                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                                width: "180px",
                                                zIndex: 999
                                              }}>
                                                <div className="fw-bold mb-1 border-bottom pb-1 text-center" style={{ fontSize: "0.85rem" }}>
                                                  {row.time} <span className="text-muted fw-normal">(Прогноз)</span>
                                                </div>

                                                <div className="text-muted mb-2 text-center" style={{ fontSize: "0.7rem", lineHeight: 1.2 }}>
                                                  <div>Фактических данных нет.</div>
                                                  <div>Расчет потерь основан</div>
                                                  <div>на средней выработке.</div>
                                                </div>

                                                <div className="d-flex justify-content-between" style={{ fontSize: "0.75rem" }}>
                                                  <span>В авто:</span>
                                                  <strong className="text-success ms-1">~{row.forecast_volume_vehicle} м³</strong>
                                                </div>
                                                <div className="d-flex justify-content-between mb-1" style={{ fontSize: "0.75rem" }}>
                                                  <span>В отвал:</span>
                                                  <strong className="text-warning ms-1">~{row.forecast_volume_ground} м³</strong>
                                                </div>
                                                <div className="d-flex justify-content-between border-top pt-1" style={{ fontSize: "0.75rem" }}>
                                                  <span>Всего:</span>
                                                  <strong>~{total_forecast} м³</strong>
                                                </div>
                                                <div className="d-flex justify-content-between mt-1" style={{ fontSize: "0.75rem" }}>
                                                  <span>Машин:</span>
                                                  <strong className="text-primary ms-1">~{row.forecast_trucks} шт.</strong>
                                                </div>
                                              </div>
                                            );
                                          }

                                          return (
                                            <div style={{ padding: "10px", background: "white", border: "1px solid #ccc", borderRadius: "6px", boxShadow: "0 4px 6px rgba(0,0,0,0.1)", minWidth: "220px", zIndex: 999 }}>
                                              <div className="fw-bold mb-2 border-bottom pb-1 text-center">
                                                {row.time} {row.is_break && <span className="text-muted fw-normal">(Перерыв)</span>}
                                              </div>
                                              <div className="d-flex justify-content-between small"><span>В самосвал:</span><strong className="text-success ms-2">{row.volume_vehicle} м³ ({row.cycles_vehicle} к.)</strong></div>
                                              <div className="d-flex justify-content-between small mb-1"><span>В отвал:</span><strong className="text-warning ms-2">{row.volume_ground} м³ ({row.cycles_ground} к.)</strong></div>
                                              <div className="d-flex justify-content-between small border-top pt-1"><span>Всего объем:</span><strong>{row.volume_total} м³</strong></div>
                                              <div className="d-flex justify-content-between small mt-1"><span>Подъездов машин:</span><strong className="text-primary ms-2">{row.trucks} шт.</strong></div>
                                              <div className="d-flex justify-content-between small"><span>Ср. цикл:</span><strong>{row.avg_cycle_sec} сек.</strong></div>
                                              <div className="d-flex justify-content-between small mt-1"><span>Простой / Связь:</span><strong className="text-danger ms-2">{Math.round((row.downtime_sec || 0) / 60)} мин.</strong></div>
                                            </div>
                                          );
                                        }}
                                      />
                                    );
                                  }}
                                </ZoomableNivoWrapper>
                              );
                            })()}
                          </div>
                        </div>
                      </CCardBody>
                    </CCard>
                  </CCol>
                </CRow>
              )}

              {/* 2. НАКОПИТЕЛЬНЫЙ ИТОГ И БАЛАНС ВРЕМЕНИ */}
              {(hasSCurveData || hasTimeBalanceData) && (
                <CRow className="mb-4 g-3">
                  {hasSCurveData && (
                    <CCol xs={12} lg={hasTimeBalanceData ? 7 : 12}>
                      <CCard className="shadow-sm border-0 h-100">
                        <CCardHeader className="bg-white fw-bold d-flex justify-content-between align-items-center">
                          <span>Накопительный итог объема</span>
                          <SafeTooltip
                            placement="left"
                            content={
                              <div style={{ maxWidth: "350px", fontSize: "0.85rem", lineHeight: "1.4", textAlign: "left" }}>
                                <strong style={{ fontSize: "0.95rem" }}>Как считается План (Норма)?</strong><br />
                                <div style={{ background: "rgba(255,255,255,0.1)", padding: "5px", borderRadius: "4px", margin: "5px 0" }}>
                                  <strong>План</strong> = (Чистое время / 30 сек) × Объем ковша
                                </div>
                                • <strong>Чистое время:</strong> время активного движения техники (без простоев).<br />
                                • <strong>30 секунд:</strong> норматив выполнения одного цикла.<br />
                                • <strong>Объем ковша:</strong> берется из настроек.
                              </div>
                            }
                          >
                            <CIcon icon={cilInfo} className="text-info ms-2" style={{ cursor: "help" }} size="lg" />
                          </SafeTooltip>
                        </CCardHeader>
                        <CCardBody className="p-0">
                          <div style={{ height: "350px", width: "100%" }}>
                            <ZoomableNivoWrapper dataLength={sCurveMapped[0]?.data.length || 0} minWindow={2}>
                              {(sIdx, eIdx) => {
                                const slicedSCurve = sCurveMapped.map(s => ({
                                  ...s,
                                  data: s.data.slice(sIdx, eIdx)
                                }));

                                return (
                                  <ResponsiveLine
                                    data={slicedSCurve}
                                    margin={{ top: 40, right: 30, bottom: 80, left: 60 }}
                                    xScale={{ type: "point" }}
                                    yScale={{ type: "linear", min: 0, max: "auto", stacked: false, reverse: false }}
                                    curve="monotoneX"
                                    axisTop={null} axisRight={null}
                                    axisBottom={{
                                      tickSize: 5,
                                      tickPadding: 5,
                                      tickRotation: -45,
                                      tickValues: slicedSCurve[0]?.data.length > 12
                                        ? slicedSCurve[0].data
                                          .filter((_, i) => i % Math.ceil(slicedSCurve[0].data.length / 10) === 0)
                                          .map(d => d.x)
                                        : undefined
                                    }}
                                    axisLeft={{ tickSize: 5, tickPadding: 5, legend: "Накопленный Объем (м³)", legendPosition: "middle", legendOffset: -50 }}
                                    colors={["#adb5bd", "#321fdb"]}
                                    lineWidth={3}
                                    pointSize={0}
                                    enablePoints={false}
                                    enableArea={true}
                                    areaOpacity={0.15}
                                    useMesh={true}
                                    crosshairType="cross"
                                    legends={[{ anchor: "top-left", direction: "row", justify: false, translateX: 10, translateY: -30, itemsSpacing: 20, itemWidth: 80, itemHeight: 20, symbolSize: 12, symbolShape: "circle" }]}
                                    tooltip={({ point }) => (
                                      <div style={{ padding: "8px 12px", background: "white", border: `1px solid ${point.serieColor}`, borderRadius: "4px", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}>
                                        <strong>{point.serieId}</strong><br />
                                        {point.data.xFormatted}: <strong style={{ color: point.serieColor }}>{point.data.yFormatted} м³</strong>
                                      </div>
                                    )}
                                  />
                                );
                              }}
                            </ZoomableNivoWrapper>
                          </div>
                        </CCardBody>
                      </CCard>
                    </CCol>
                  )}

                  {hasTimeBalanceData && (
                    <CCol xs={12} lg={hasSCurveData ? 5 : 12}>
                      <CCard className="shadow-sm border-0 h-100">
                        <CCardHeader className="bg-white fw-bold border-bottom-0 text-center">
                          Баланс времени работы
                        </CCardHeader>
                        <CCardBody className="p-0">
                          <div style={{ height: "350px" }}>
                            {(() => {
                              const tWork = analyticsData.kpis?.tWork || 0;
                              const durs = analyticsData.durations_by_class_sec || {};
                              const engineOn = durs.actual_downtime || 0;
                              const engineOff = durs.no_data || 0;
                              const totalDowntime = engineOn + engineOff;

                              const timeBarData = [
                                {
                                  id: "Полезное время",
                                  label: "Полезное",
                                  value: tWork,
                                  color: "#2eb85c",
                                  desc: "Сумма временных интервалов, в которых техника совершала активную работу."
                                },
                                {
                                  id: "Всего простоев",
                                  label: "Простои",
                                  value: totalDowntime,
                                  color: "#e55353",
                                  desc: "Сумма временных интервалов, в которых техника не совершала активной работы."
                                },
                                {
                                  id: "Простой с работающим двигателем",
                                  label: "Двиг. вкл.",
                                  value: engineOn,
                                  color: "#f9b115",
                                  desc: "Сумма простоев, в которых двигатель работал (масса в рамках бортовой электроцепи экскаватора включена)."
                                },
                                {
                                  id: "Простой с выключенным двигателем",
                                  label: "Двиг. выкл.",
                                  value: engineOff,
                                  color: "#63c2de",
                                  desc: "Сумма простоев, в которых двигатель был заглушен (масса в рамках бортовой электроцепи экскаватора выключена)."
                                },
                              ];

                              const BreakdownLayer = ({ bars }) => {
                                const barOn = bars.find(b => b.data.indexValue === "Двиг. вкл.");
                                const barOff = bars.find(b => b.data.indexValue === "Двиг. выкл.");
                                if (!barOn || !barOff) return null;

                                const startX = barOn.x;
                                const endX = barOff.x + barOff.width;
                                const midX = startX + (endX - startX) / 2;

                                const highestBarY = Math.min(barOn.y, barOff.y);
                                const bracketY = highestBarY - 25;

                                return (
                                  <g>
                                    <path
                                      d={`M ${startX + barOn.width / 2},${highestBarY - 10} L ${startX + barOn.width / 2},${bracketY} L ${endX - barOff.width / 2},${bracketY} L ${endX - barOff.width / 2},${highestBarY - 10}`}
                                      fill="none"
                                      stroke="#adb5bd"
                                      strokeWidth={1.5}
                                      strokeDasharray="4 4"
                                    />
                                    <rect x={midX - 70} y={bracketY - 10} width={140} height={20} fill="white" rx={4} />
                                    <text
                                      x={midX}
                                      y={bracketY + 4}
                                      textAnchor="middle"
                                      fill="#6c757d"
                                      fontSize="11"
                                      fontWeight="bold"
                                    >
                                      Детализация простоев
                                    </text>
                                  </g>
                                );
                              };

                              return (
                                <ResponsiveBar
                                  data={timeBarData}
                                  keys={["value"]}
                                  indexBy="label"
                                  margin={{ top: 50, right: 20, bottom: 40, left: 60 }}
                                  padding={0.3}
                                  valueScale={{ type: "linear" }}
                                  colors={({ data }) => data.color}
                                  colorBy="indexValue"
                                  axisTop={null}
                                  axisRight={null}
                                  axisBottom={{
                                    tickSize: 5,
                                    tickPadding: 5,
                                    tickRotation: 0,
                                  }}
                                  axisLeft={{
                                    tickSize: 5,
                                    tickPadding: 5,
                                    legend: "Время (часы)",
                                    legendPosition: "middle",
                                    legendOffset: -50,
                                    format: (v) => `${(v / 3600).toFixed(1)}ч`,
                                  }}
                                  enableLabel={false}
                                  animate={true}
                                  motionConfig="gentle"
                                  layers={["grid", "axes", "bars", BreakdownLayer, "markers"]}
                                  tooltip={({ data }) => (
                                    <div
                                      style={{
                                        padding: "12px",
                                        background: "white",
                                        border: `1px solid ${data.color}`,
                                        borderRadius: "6px",
                                        boxShadow: "0 4px 15px rgba(0,0,0,0.15)",
                                        maxWidth: "260px",
                                        whiteSpace: "normal"
                                      }}
                                    >
                                      <div style={{ color: data.color, fontWeight: "bold", fontSize: "1rem", borderBottom: "1px solid #eee", paddingBottom: "6px", marginBottom: "8px" }}>
                                        {data.id}: <br /><span className="text-dark fs-5">{formatDuration(data.value)}</span>
                                      </div>
                                      <div style={{ fontSize: "0.8rem", color: "#6c757d", lineHeight: "1.3" }}>
                                        {data.desc}
                                      </div>
                                    </div>
                                  )}
                                />
                              );
                            })()}
                          </div>
                          <div className="p-3 text-center border-top">
                            <CButton
                              color="danger"
                              variant="outline"
                              size="sm"
                              className="fw-bold shadow-sm"
                              onClick={() => setDowntimeModal({ visible: true, data: null })}
                            >
                              <CIcon icon={cilWarning} className="me-1" /> Журнал простоев (Классифицировать)
                            </CButton>
                          </div>
                        </CCardBody>
                      </CCard>
                    </CCol>
                  )}
                </CRow>
              )}

              {/* 3. СОСТАВЛЯЮЩИЕ ЦИКЛА ЭКСКАВАЦИИ */}
              {hasCycleComponentsData && (
                <CRow className="mb-4">
                  <CCol xs={12}>
                    <CCard className="shadow-sm border-0 h-100">
                      <CCardHeader className="bg-white fw-bold">
                        Составляющие цикла экскавации (сек)
                      </CCardHeader>
                      <CCardBody>
                        <div style={{ width: "100%", overflowX: "auto", overflowY: "hidden", position: "relative" }}>
                          <div style={{ height: "350px", width: Math.max(100, analyticsData.extra_dash_charts.cycle_components.length * 5) + "%", minWidth: "100%" }}>
                            <ResponsiveBar
                              data={analyticsData.extra_dash_charts.cycle_components}
                              keys={['Возврат и копание', 'Поворот на разгрузку', 'Процесс разгрузки', 'Микро-простои']}
                              indexBy="time"
                              margin={{ top: 20, right: 180, bottom: 60, left: 60 }}
                              padding={0.3}
                              colors={['#2eb85c', '#f9b115', '#c8ced3', '#e55353']}
                              valueScale={{ type: 'linear' }}
                              indexScale={{ type: 'band', round: true }}
                              axisTop={null}
                              axisRight={null}
                              axisBottom={{
                                tickSize: 5,
                                tickPadding: 5,
                                tickRotation: -45,
                              }}
                              axisLeft={{
                                tickSize: 5,
                                tickPadding: 5,
                                legend: "Секунды",
                                legendPosition: "middle",
                                legendOffset: -45,
                              }}
                              legends={[
                                {
                                  dataFrom: 'keys',
                                  anchor: 'bottom-right',
                                  direction: 'column',
                                  justify: false,
                                  translateX: 160,
                                  translateY: 0,
                                  itemsSpacing: 10,
                                  itemWidth: 150,
                                  itemHeight: 20,
                                  symbolSize: 15,
                                }
                              ]}
                              tooltip={({ id, value, color, indexValue }) => (
                                <div style={{ padding: "8px", background: "white", border: `1px solid ${color}`, borderRadius: "4px" }}>
                                  <strong>{indexValue}</strong><br />
                                  {id}: <strong style={{ color }}>{value} сек</strong>
                                </div>
                              )}
                            />
                          </div>
                        </div>
                      </CCardBody>
                    </CCard>
                  </CCol>
                </CRow>
              )}

              {/* 4. ДИНАМИКА КОВШЕЙ И РАСПРЕДЕЛЕНИЕ (ПАРЕТО) */}
              {(hasBucketsPerTruckData || hasParetoData) && (
                <CRow className="mb-4 g-3">
                  {hasBucketsPerTruckData && (
                    <CCol xs={12} lg={hasParetoData ? 6 : 12}>
                      <CCard className="shadow-sm border-0 h-100">
                        <CCardHeader className="bg-white fw-bold d-flex justify-content-between align-items-center">
                          <span>Динамика количества ковшей на 1 АС</span>
                          <div className="d-flex align-items-center">
                            <label className="me-2 fw-bold small text-muted mb-0">План (ковшей):</label>
                            <input
                              type="number"
                              className="form-control form-control-sm text-center fw-bold text-danger"
                              style={{ width: "70px" }}
                              value={targetBuckets}
                              step="0.1"
                              min="0"
                              onChange={(e) => {
                                let val = e.target.value;
                                if (Number(val) < 0) val = 0;
                                setTargetBuckets(val);

                                if (saveTimerRef.current) {
                                  clearTimeout(saveTimerRef.current);
                                }

                                saveTimerRef.current = setTimeout(() => {
                                  saveTargetBuckets(val);
                                }, 5000);
                              }}
                              title={
                                localStorage.getItem("is_staff") === "true"
                                  ? "Сохранится автоматически через 5 сек после ввода"
                                  : "Изменение доступно только администратору"
                              }
                              disabled={localStorage.getItem("is_staff") !== "true"}
                            />
                          </div>
                        </CCardHeader>
                        <CCardBody>
                          <div style={{ width: "100%", overflowX: "auto", overflowY: "hidden", position: "relative" }}>
                            {(() => {
                              const bucketsData = analyticsData.extra_dash_charts?.buckets_per_truck || [];
                              const maxBucketVal = Math.max(
                                Number(targetBuckets) || 5,
                                ...bucketsData.map(d => Number(d.avg_buckets) || 0)
                              );
                              const yMax = Math.max(10, Math.ceil(maxBucketVal * 1.3));

                              return (
                                <div style={{ height: "320px", width: Math.max(100, bucketsData.length * 4) + "%", minWidth: "100%" }}>
                                  <ResponsiveLine
                                    data={[
                                      {
                                        id: "Факт (ковшей/АС)",
                                        color: "#321fdb",
                                        data: bucketsData.map(d => ({ x: d.time, y: d.avg_buckets }))
                                      }
                                    ]}
                                    margin={{ top: 35, right: 35, bottom: 65, left: 45 }}
                                    xScale={{ type: "point" }}
                                    yScale={{ type: "linear", min: 0, max: yMax, stacked: false, reverse: false }}
                                    curve="monotoneX"
                                    axisBottom={{
                                      tickRotation: -45,
                                      tickSize: 5,
                                      tickPadding: 5
                                    }}
                                    axisLeft={{ legend: "Кол-во ковшей", legendPosition: "middle", legendOffset: -38 }}
                                    colors={["#321fdb"]}
                                    lineWidth={3}
                                    pointSize={8}
                                    useMesh={true}
                                    tooltip={({ point }) => (
                                      <div style={{ padding: "6px 10px", background: "white", border: "1px solid #321fdb", borderRadius: "6px", boxShadow: "0 2px 8px rgba(0,0,0,0.15)", fontSize: "12px" }}>
                                        <strong>{point.data.x}</strong><br />
                                        <span style={{ color: "#321fdb", fontWeight: "bold" }}>
                                          Ковшей на 1 АС: {point.data.y} шт.
                                        </span>
                                      </div>
                                    )}
                                    layers={[
                                      'grid',
                                      ({ xScale, innerHeight, innerWidth }) => {
                                        const step = xScale.step();
                                        return (
                                          <g>
                                            <defs>
                                              <clipPath id="idle-stripes-clip">
                                                <rect x={0} y={0} width={innerWidth} height={innerHeight} />
                                              </clipPath>
                                              <pattern id="diagonal-stripes-idle" width="8" height="8" patternTransform="rotate(45)" patternUnits="userSpaceOnUse">
                                                <rect width="8" height="8" fill="transparent" />
                                                <line x1="0" y1="0" x2="0" y2="8" stroke="rgba(0,0,0,0.06)" strokeWidth="4" />
                                              </pattern>
                                            </defs>
                                            <g clipPath="url(#idle-stripes-clip)">
                                              {bucketsData.map((d, i) => {
                                                if (d.avg_buckets === null) {
                                                  const x = xScale(d.time);
                                                  if (x === undefined) return null;
                                                  return (
                                                    <rect
                                                      key={`idle-${i}`}
                                                      x={x - step / 2}
                                                      y={0}
                                                      width={step}
                                                      height={innerHeight}
                                                      fill="url(#diagonal-stripes-idle)"
                                                    />
                                                  );
                                                }
                                                return null;
                                              })}
                                            </g>
                                          </g>
                                        );
                                      },
                                      'markers',
                                      'axes',
                                      'areas',
                                      'crosshair',
                                      'lines',
                                      'points',
                                      'slices',
                                      'mesh',
                                      'legends'
                                    ]}
                                    markers={[
                                      {
                                        axis: "y",
                                        value: Number(targetBuckets) || 0,
                                        lineStyle: { stroke: "#e55353", strokeWidth: 2, strokeDasharray: "4 4" },
                                        legend: `План: ${targetBuckets}`,
                                        legendPosition: "top-left",
                                        legendOrientation: "horizontal",
                                        textStyle: { fill: "#e55353", fontWeight: "bold", fontSize: 12 }
                                      }
                                    ]}
                                  />
                                </div>
                              );
                            })()}
                          </div>
                        </CCardBody>
                      </CCard>
                    </CCol>
                  )}

                  {hasParetoData && (
                    <CCol xs={12} lg={hasBucketsPerTruckData ? 6 : 12}>
                      <CCard className="shadow-sm border-0 h-100">
                        <CCardHeader className="bg-white fw-bold">
                          Распределение количества ковшей на 1 АС
                        </CCardHeader>
                        <CCardBody>
                          <div style={{ height: "320px", width: "100%" }}>
                            {(() => {
                              const paretoData = analyticsData.extra_dash_charts.pareto_data;
                              const maxFreq = Math.max(1, ...paretoData.map(d => d.frequency));

                              const ParetoLineLayer = ({ xScale, innerHeight }) => {
                                const linePoints = paretoData.map(d => {
                                  const xVal = xScale(d.buckets);
                                  if (xVal === undefined) return null;
                                  const x = xVal + xScale.bandwidth() / 2;
                                  const y = innerHeight - (d.cumulative_pct / 100) * innerHeight;
                                  return { x, y, pct: d.cumulative_pct };
                                }).filter(Boolean);

                                if (linePoints.length === 0) return null;
                                const linePath = linePoints.map((p, i) => (i === 0 ? `M ${p.x},${p.y}` : `L ${p.x},${p.y}`)).join(" ");

                                return (
                                  <g>
                                    <path d={linePath} fill="none" stroke="#e55353" strokeWidth={3} />
                                    {linePoints.map((p, i) => (
                                      <g key={`pareto-pt-${i}`}>
                                        <circle cx={p.x} cy={p.y} r={4} fill="#e55353" stroke="#fff" strokeWidth={2} />
                                        <text x={p.x} y={p.y - 10} textAnchor="middle" fill="#e55353" fontSize="11" fontWeight="bold">
                                          {p.pct}
                                        </text>
                                      </g>
                                    ))}
                                  </g>
                                );
                              };

                              return (
                                <ResponsiveBar
                                  data={paretoData}
                                  keys={['frequency']}
                                  indexBy="buckets"
                                  margin={{ top: 30, right: 60, bottom: 50, left: 50 }}
                                  padding={0.3}
                                  colors={['#321fdb']}
                                  axisTop={null}
                                  axisRight={{
                                    tickValues: 5,
                                    tickFormat: v => `${Math.round((v / maxFreq) * 100)}%`,
                                    legend: "Накопительный %",
                                    legendPosition: "middle",
                                    legendOffset: 45
                                  }}
                                  axisBottom={{ legend: "Количество ковшей в рейсе", legendPosition: "middle", legendOffset: 40 }}
                                  axisLeft={{ legend: "Кол-во рейсов", legendPosition: "middle", legendOffset: -40 }}
                                  labelSkipWidth={12}
                                  labelSkipHeight={12}
                                  labelTextColor="#ffffff"
                                  layers={['grid', 'axes', 'bars', ParetoLineLayer, 'markers']}
                                  tooltip={({ value, indexValue }) => (
                                    <div style={{ padding: "8px", background: "white", border: `1px solid #ccc`, borderRadius: "4px" }}>
                                      Рейсов по <strong>{indexValue} ковшей</strong>: {value} шт.
                                    </div>
                                  )}
                                />
                              );
                            })()}
                          </div>
                        </CCardBody>
                      </CCard>
                    </CCol>
                  )}
                </CRow>
              )}

              {/* 5. ЭФФЕКТИВНОСТЬ ПОГРУЗКИ (УКВ) */}
              {hasUkvData && (
                <CRow className="mb-4">
                  <CCol xs={12}>
                    <CCard className="shadow-sm border-0 h-100">
                      <CCardHeader className="bg-white py-3 border-bottom d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
                        <div className="d-flex align-items-center">
                          <span className="fw-bold fs-6 text-dark me-2">
                            Эффективность погрузки (Отклонения)
                          </span>
                          <SafeTooltip content="Задайте допустимые диапазоны времени для выявления аномалий. Если поля оставить пустыми, система рассчитывает норму автоматически (±30% от средних значений по парку).">
                            <CIcon icon={cilInfo} className="text-info" style={{ cursor: "help" }} />
                          </SafeTooltip>
                        </div>

                        <div className="d-flex align-items-center gap-3 bg-light px-3 py-2 rounded-3 border">
                          <div className="form-check form-switch mb-0 d-flex align-items-center">
                            <input
                              className="form-check-input me-2 m-0"
                              type="checkbox"
                              id="showLine"
                              checked={ukvSettings.showLine}
                              onChange={(e) => setUkvSettings((p) => ({ ...p, showLine: e.target.checked }))}
                              style={{ cursor: "pointer" }}
                            />
                            <label className="form-check-label small fw-bold text-dark" htmlFor="showLine" style={{ cursor: "pointer" }}>
                              УКВ (Линия)
                            </label>
                          </div>

                          <div className="vr opacity-25" style={{ height: "18px" }}></div>

                          <div className="form-check form-switch mb-0 d-flex align-items-center">
                            <input
                              className="form-check-input me-2 m-0"
                              type="checkbox"
                              id="showBars"
                              checked={ukvSettings.showBars}
                              onChange={(e) => setUkvSettings((p) => ({ ...p, showBars: e.target.checked }))}
                              style={{ cursor: "pointer" }}
                            />
                            <label className="form-check-label small fw-bold text-dark" htmlFor="showBars" style={{ cursor: "pointer" }}>
                              Время заезда (Столбцы)
                            </label>
                          </div>
                        </div>
                      </CCardHeader>

                      <CCardBody className="p-3">
                        <div className="bg-light bg-opacity-75 border rounded-3 p-3 mb-3">
                          <div className="d-flex flex-column flex-xl-row align-items-xl-center justify-content-between gap-3">

                            <div className="d-flex align-items-center gap-2">
                              <span className="badge bg-secondary bg-opacity-10 text-secondary border px-2 py-1 fw-bold text-uppercase" style={{ fontSize: "0.75rem", letterSpacing: "0.5px" }}>
                                Нормы времени
                              </span>
                              <span className="small text-muted">
                                (допустимый диапазон «от — до»):
                              </span>
                            </div>

                            <div className="d-flex flex-wrap align-items-center gap-3">
                              <div className="d-flex align-items-center bg-white border rounded-3 px-3 py-1 shadow-sm gap-2">
                                <span className="fw-bold small text-dark text-nowrap">
                                  ⏱️ Ковш (УКВ):
                                </span>
                                <div className="d-flex align-items-center gap-1">
                                  <span className="text-muted small">от</span>
                                  <input
                                    type="number"
                                    className="form-control form-control-sm text-center fw-bold border-success"
                                    style={{ width: "65px", color: "#2eb85c" }}
                                    placeholder={(ukvData?.bounds.uMin || 0).toFixed(0)}
                                    value={ukvSettings.ukvMin}
                                    onChange={(e) => setUkvSettings((p) => ({ ...p, ukvMin: e.target.value }))}
                                    title="Минимальное нормативное время одного цикла (сек)"
                                  />
                                  <span className="text-muted small px-1">—</span>
                                  <span className="text-muted small">до</span>
                                  <input
                                    type="number"
                                    className="form-control form-control-sm text-center fw-bold border-danger"
                                    style={{ width: "65px", color: "#e55353" }}
                                    placeholder={(ukvData?.bounds.uMax || 0).toFixed(0)}
                                    value={ukvSettings.ukvMax}
                                    onChange={(e) => setUkvSettings((p) => ({ ...p, ukvMax: e.target.value }))}
                                    title="Максимальное нормативное время одного цикла (сек)"
                                  />
                                  <span className="text-muted small ms-1">сек</span>
                                </div>
                              </div>

                              <div className="d-flex align-items-center bg-white border rounded-3 px-3 py-1 shadow-sm gap-2">
                                <span className="fw-bold small text-dark text-nowrap">
                                  🚛 Заезд:
                                </span>
                                <div className="d-flex align-items-center gap-1">
                                  <span className="text-muted small">от</span>
                                  <input
                                    type="number"
                                    className="form-control form-control-sm text-center fw-bold border-success"
                                    style={{ width: "65px", color: "#2eb85c" }}
                                    placeholder={(ukvData?.bounds.sMin || 0).toFixed(0)}
                                    value={ukvSettings.sessionMin}
                                    onChange={(e) => setUkvSettings((p) => ({ ...p, sessionMin: e.target.value }))}
                                    title="Минимальное время полной загрузки машины (сек)"
                                  />
                                  <span className="text-muted small px-1">—</span>
                                  <span className="text-muted small">до</span>
                                  <input
                                    type="number"
                                    className="form-control form-control-sm text-center fw-bold border-danger"
                                    style={{ width: "65px", color: "#e55353" }}
                                    placeholder={(ukvData?.bounds.sMax || 0).toFixed(0)}
                                    value={ukvSettings.sessionMax}
                                    onChange={(e) => setUkvSettings((p) => ({ ...p, sessionMax: e.target.value }))}
                                    title="Максимальное время полной загрузки машины (сек)"
                                  />
                                  <span className="text-muted small ms-1">сек</span>
                                </div>
                              </div>

                              {(ukvSettings.ukvMin || ukvSettings.ukvMax || ukvSettings.sessionMin || ukvSettings.sessionMax) && (
                                <CButton
                                  color="secondary"
                                  variant="outline"
                                  size="sm"
                                  className="fw-semibold px-2 py-1"
                                  onClick={() => setUkvSettings((p) => ({ ...p, ukvMin: "", ukvMax: "", sessionMin: "", sessionMax: "" }))}
                                  title="Сбросить к автоматическому расчету (±30% от средней нормы)"
                                >
                                  Сбросить
                                </CButton>
                              )}
                            </div>

                          </div>
                        </div>

                        {ukvData.isLongPeriod && (
                          <div className="alert alert-info py-2 d-flex align-items-center mb-3 border-info border-opacity-50">
                            <CIcon icon={cilInfo} className="text-info flex-shrink-0 me-3" size="xl" />
                            <span className="small" style={{ lineHeight: "1.4" }}>
                              <strong>Внимание: Выбран период более 48 часов.</strong><br />
                              В целях оптимизации отображения данные на этом графике <strong>усреднены по дням</strong>. Из-за усреднения локальные аномалии отдельных самосвалов сглаживаются и могут быть не видны.
                              Чтобы детально проанализировать отклонения каждого заезда, выберите период <strong>«Сегодня»</strong>, <strong>«Вчера»</strong> или любой интервал <strong>менее 2 суток</strong>.
                            </span>
                          </div>
                        )}

                        {ukvData.anomalies.length > 0 ? (
                          <div className="alert alert-danger py-2 d-flex align-items-center flex-wrap gap-2 mb-3 border-danger border-opacity-50">
                            <CIcon icon={cilWarning} className="text-danger flex-shrink-0" />
                            <span className="fw-bold small me-2">
                              Аномалий: {ukvData.anomalies.length}. Нажмите для перехода:
                            </span>
                            {ukvData.anomalies.slice(0, 8).map((anom, i) => (
                              <CBadge
                                key={i} color="danger" shape="rounded-pill" style={{ cursor: 'pointer' }}
                                onClick={() => scrollToAnomaly(anom.index)}
                                title={anom.anomalyReasons.join('\n')}
                              >
                                {anom.displayLabel}
                              </CBadge>
                            ))}
                            {ukvData.anomalies.length > 8 && <span className="small text-muted fw-bold">+{ukvData.anomalies.length - 8} еще</span>}
                          </div>
                        ) : (
                          <div className="alert alert-success py-2 d-flex align-items-center mb-3">
                            <span className="fw-bold small">Отклонений не выявлено. Вся техника работает в пределах установленных норм.</span>
                          </div>
                        )}

                        <div style={{ position: "relative", width: "100%", border: "1px solid #ebedef", borderRadius: "8px", overflow: "hidden" }}>

                          <div style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", pointerEvents: "none", zIndex: 10 }}>

                            {ukvSettings.showBars && (
                              <>
                                <div style={{ ...overlayLabelStyle, top: getSessionY(ukvData.bounds.sMin / 60), left: "60px", color: "#2eb85c" }}>
                                  Мин. заезд ({(ukvData.bounds.sMin / 60).toFixed(1)}м)
                                </div>
                                <div style={{ ...overlayLabelStyle, top: getSessionY(ukvData.bounds.sMax / 60), left: "60px", color: "#e55353" }}>
                                  Макс. заезд ({(ukvData.bounds.sMax / 60).toFixed(1)}м)
                                </div>
                              </>
                            )}

                            {ukvSettings.showLine && (
                              <>
                                <div style={{ ...overlayLabelStyle, top: getUkvY(ukvData.bounds.uMin), right: "20px", color: "#2eb85c" }}>
                                  УКВ Мин ({ukvData.bounds.uMin.toFixed(0)}с)
                                </div>
                                <div style={{ ...overlayLabelStyle, top: getUkvY(ukvData.bounds.uMax), right: "20px", color: "#e55353" }}>
                                  УКВ Макс ({ukvData.bounds.uMax.toFixed(0)}с)
                                </div>
                              </>
                            )}
                          </div>

                          <div
                            ref={ukvScrollRef}
                            style={{ width: "100%", overflowX: "auto", overflowY: "hidden", position: "relative" }}
                          >
                            <div style={{ height: `${ukvChartHeight}px`, width: Math.max(100, ukvData.data.length * 6) + "%", minWidth: "100%" }}>
                              <ResponsiveBar
                                data={ukvData.data.map((d) => ({ ...d, isHighlighted: d.index === highlightedUkvIndex }))}
                                keys={["durationMin"]}
                                indexBy="timeLabel"
                                margin={{ top: ukvMarginTop, right: 20, bottom: ukvMarginBottom, left: 50 }}
                                padding={0.4}
                                yScale={{ type: "linear", max: ukvMaxSessionMin }}
                                colors={({ data }) => {
                                  if (!ukvSettings.showBars) return "transparent";
                                  return data.isHighlighted ? "#e55353" : "rgba(50, 31, 219, 0.6)";
                                }}
                                borderRadius={4}
                                axisTop={null}
                                axisRight={null}
                                axisBottom={{
                                  tickSize: 5,
                                  tickPadding: 5,
                                  tickRotation: -45,
                                  legend: ukvData.isLongPeriod ? "Дни" : "Хронология заездов",
                                  legendPosition: "middle",
                                  legendOffset: 60,
                                  format: (v) => {
                                    if (typeof v === "string" && v.includes(" (#")) {
                                      return v.split(" (#")[0];
                                    }
                                    return v;
                                  },
                                }}
                                axisLeft={{
                                  tickSize: 5,
                                  tickPadding: 5,
                                  tickRotation: 0,
                                  legend: "Общее время (мин)",
                                  legendPosition: "middle",
                                  legendOffset: -40,
                                }}
                                enableGridY={true}
                                labelSkipHeight={12}
                                labelTextColor={ukvSettings.showBars ? "#ffffff" : "transparent"}
                                layers={["grid", "axes", "bars", CustomUkvLayer]}
                                onClick={(node) => {
                                  if (typeof onTimeJump === "function" && node.data.startMs && !ukvData.isLongPeriod) {
                                    window.scrollTo({ top: 0, behavior: "smooth" });
                                    const fakeLocalMs = getFakeLocalDate(
                                      new Date(node.data.startMs).toISOString(),
                                      device?.timezone_offset || 0
                                    ).getTime();
                                    onTimeJump({ exactTime: fakeLocalMs, item: { label: node.data.displayLabel } });
                                  }
                                }}
                                theme={{ tooltip: { container: { cursor: "pointer" } } }}
                                tooltip={({ data }) => {
                                  const isSessAnom = data.durationSec < ukvData.bounds.sMin || data.durationSec > ukvData.bounds.sMax;
                                  const isUkvAnom = data.ukv < ukvData.bounds.uMin || data.ukv > ukvData.bounds.uMax;

                                  return (
                                    <div style={{ padding: "10px", background: "white", border: `2px solid ${data.isAnomaly ? "#e55353" : "#2eb85c"}`, borderRadius: "6px", boxShadow: "0 4px 15px rgba(0,0,0,0.1)", cursor: ukvData.isLongPeriod ? "default" : "pointer" }}>
                                      <div className="fw-bold border-bottom pb-1 mb-2 text-center text-dark">
                                        {data.displayLabel} <span className="text-muted fw-normal">| {data.title}</span>
                                      </div>
                                      <div className="small d-flex justify-content-between mb-1">
                                        <span className="text-muted">{data.isAggregated ? "Ср. время заезда:" : "Время заезда:"}</span>
                                        <strong className={`ms-3 ${isSessAnom ? "text-danger" : "text-dark"}`}>{formatDuration(data.durationSec)}</strong>
                                      </div>
                                      <div className="small d-flex justify-content-between mb-1">
                                        <span className="text-muted">{data.isAggregated ? "Ср. ковшей отгружено:" : "Ковшей отгружено:"}</span>
                                        <strong className="ms-3">{data.cycles} шт.</strong>
                                      </div>
                                      <div className="small d-flex justify-content-between mt-2 pt-2 border-top">
                                        <span className="fw-bold">{data.isAggregated ? "Средний УКВ:" : "УКВ (сек/ковш):"}</span>
                                        <strong className={`ms-3 fs-6 ${isUkvAnom ? "text-danger" : "text-success"}`}>
                                          {data.ukv} сек.
                                        </strong>
                                      </div>
                                      {data.isAnomaly && (
                                        <div className="mt-2 pt-2 border-top">
                                          {data.anomalyReasons.map((r, idx) => (
                                            <div key={idx} className="text-danger small fw-bold" style={{ fontSize: "0.7rem" }}>- {r}</div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  );
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      </CCardBody>
                    </CCard>
                  </CCol>
                </CRow>
              )}
            </>
          )}
        </div>
      )}

      {/* --- ТАЙМЛАЙН РАБОТЫ (ОСТАЕТСЯ ВСЕГДА НА ВИДУ) --- */}
      <div className="mb-4">
        {timelineComponent}
      </div>

      {/* --- КАРТОЧКИ: РАБОТА В САМОСВАЛ И В ОТВАЛ --- */}
      <CRow className="g-4 mb-4">
        {/* КАРТОЧКА: РАБОТА В САМОСВАЛ */}
        <CCol md={6}>
          <CCard className="h-100 shadow-sm border-0 overflow-hidden">
            <CCardHeader className="bg-light border-bottom-0 py-3 d-flex align-items-center">
              <div
                className="shadow-sm"
                style={{ width: "14px", height: "14px", backgroundColor: classIdToMeta[1].color, borderRadius: "50%", marginRight: "10px" }}
              />
              <span className="fw-bold fs-6 text-dark">Работа в самосвал</span>
            </CCardHeader>
            <CCardBody className="p-0 d-flex flex-column">
              <div className="p-3 bg-white flex-grow-1">
                <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                  <SafeTooltip content={t("tooltips.cycles_vehicle")}>
                    <span className="text-muted small fw-semibold" style={hintStyle}>{t("analysis.cycles_vehicle")}</span>
                  </SafeTooltip>
                  <span className="fs-5 fw-bold" style={{ color: classIdToMeta[1].color }}>
                    {analyticsData.cycle_count_vehicle}
                    {analyticsData.truck_stats_summary && Object.keys(analyticsData.truck_stats_summary).length > 0 && (
                      <span className="text-muted fw-semibold ms-2" style={{ fontSize: "0.6em" }}>
                        ({t("analysis.fact")}: {actualTruckTotals.cycles})
                      </span>
                    )}
                  </span>
                </div>

                <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                  <span className="text-muted small fw-semibold">{t("analysis.to_vehicle_label")}</span>
                  <div className="text-end">
                    <strong className="fs-6 text-dark">
                      {Number(
                        isTwinSolid ? (analyticsData.ai_volume_vehicle || 0) * globalSolidRatio
                          : isTwin ? analyticsData.ai_volume_vehicle
                            : isSolid ? analyticsData.solid_volume_vehicle
                              : analyticsData.estimated_volume_vehicle || 0
                      ).toFixed(1)} м³
                    </strong>
                    <span className="text-muted mx-2">/</span>
                    <strong className="fs-6 text-dark">
                      {(Number(isTwin || isTwinSolid ? analyticsData.ai_mass_vehicle : analyticsData.estimated_mass_vehicle || 0) / 1000).toFixed(1)} т
                    </strong>
                  </div>
                </div>

                {(() => {
                  const v_empty = analyticsData.averages.vehicle.empty_sec || 0;
                  const v_full = analyticsData.averages.vehicle.full_sec || 0;
                  const v_unload = analyticsData.averages.vehicle.unload_sec || 0;
                  const v_delay = analyticsData.averages.vehicle.delay_sec || 0;
                  const v_actual = (v_empty + v_full + v_unload + v_delay).toFixed(1);
                  const v_total_calc = parseFloat(v_actual) > 0 ? parseFloat(v_actual) : 1;

                  const pctEmpty = (v_empty / v_total_calc) * 100;
                  const pctFull = (v_full / v_total_calc) * 100;
                  const pctUnload = (v_unload / v_total_calc) * 100;
                  const pctDelay = (v_delay / v_total_calc) * 100;

                  return (
                    <div className="mt-4">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <SafeTooltip content="Фактическое время цикла (общее время погрузки / кол-во ковшей)">
                          <span className="fw-bold text-dark small text-uppercase" style={hintStyle}>Ср. время цикла:</span>
                        </SafeTooltip>
                        <strong className="fs-5 text-dark">{v_actual} сек</strong>
                      </div>

                      <div className="d-flex w-100 rounded overflow-hidden mb-3 shadow-sm" style={{ height: "12px", backgroundColor: "#e9ecef" }}>
                        {pctEmpty > 0 && <div style={{ width: `${pctEmpty}%`, backgroundColor: "#3399ff" }} title={`Копание: ${v_empty}с`} />}
                        {pctFull > 0 && <div style={{ width: `${pctFull}%`, backgroundColor: "#ff9933" }} title={`Поворот: ${v_full}с`} />}
                        {pctUnload > 0 && <div style={{ width: `${pctUnload}%`, backgroundColor: "#2eb85c" }} title={`Разгрузка: ${v_unload}с`} />}
                        {pctDelay > 0 && <div style={{ width: `${pctDelay}%`, backgroundColor: "#e55353" }} title={`Задержки: ${v_delay}с`} />}
                      </div>

                      <div className="bg-light rounded p-3">
                        <div className="d-flex justify-content-between text-muted small mb-2">
                          <span className="d-flex align-items-center"><span className="d-inline-block rounded-circle me-2 shadow-sm" style={{ width: '10px', height: '10px', backgroundColor: '#3399ff' }}></span> Копание:</span>
                          <span className="fw-bold text-dark">{v_empty} сек</span>
                        </div>
                        <div className="d-flex justify-content-between text-muted small mb-2">
                          <span className="d-flex align-items-center"><span className="d-inline-block rounded-circle me-2 shadow-sm" style={{ width: '10px', height: '10px', backgroundColor: '#ff9933' }}></span> Поворот:</span>
                          <span className="fw-bold text-dark">{v_full} сек</span>
                        </div>
                        <div className="d-flex justify-content-between text-muted small mb-2">
                          <span className="d-flex align-items-center"><span className="d-inline-block rounded-circle me-2 shadow-sm" style={{ width: '10px', height: '10px', backgroundColor: '#2eb85c' }}></span> Разгрузка:</span>
                          <span className="fw-bold text-dark">{v_unload} сек</span>
                        </div>
                        {v_delay > 0 && (
                          <div className="d-flex justify-content-between text-muted small mt-3 pt-2 border-top">
                            <span className="d-flex align-items-center text-danger fw-bold"><span className="d-inline-block rounded-circle me-2 shadow-sm" style={{ width: '10px', height: '10px', backgroundColor: '#e55353' }}></span> Микро-задержки:</span>
                            <span className="fw-bold text-danger">{v_delay} сек</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </CCardBody>
          </CCard>
        </CCol>

        {/* КАРТОЧКА: РАБОТА В ОТВАЛ */}
        <CCol md={6}>
          <CCard className="h-100 shadow-sm border-0 overflow-hidden">
            <CCardHeader className="bg-light border-bottom-0 py-3 d-flex align-items-center">
              <div
                className="shadow-sm"
                style={{ width: "14px", height: "14px", backgroundColor: classIdToMeta[2].color, borderRadius: "50%", marginRight: "10px" }}
              />
              <span className="fw-bold fs-6 text-dark">Работа в отвал (землю)</span>
            </CCardHeader>
            <CCardBody className="p-0 d-flex flex-column">
              <div className="p-3 bg-white flex-grow-1">
                <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                  <SafeTooltip content={t("tooltips.cycles_ground")}>
                    <span className="text-muted small fw-semibold" style={hintStyle}>{t("analysis.cycles_ground")}</span>
                  </SafeTooltip>
                  <span className="fs-5 fw-bold" style={{ color: classIdToMeta[2].color }}>
                    {analyticsData.cycle_count_ground}
                  </span>
                </div>

                <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                  <span className="text-muted small fw-semibold">{t("analysis.to_ground_label")}</span>
                  <div className="text-end">
                    <strong className="fs-6 text-dark">
                      {Number(
                        isTwinSolid ? (analyticsData.ai_volume_ground || 0) * globalSolidRatio
                          : isTwin ? analyticsData.ai_volume_ground
                            : isSolid ? analyticsData.solid_volume_ground
                              : analyticsData.estimated_volume_ground || 0
                      ).toFixed(1)} м³
                    </strong>
                    <span className="text-muted mx-2">/</span>
                    <strong className="fs-6 text-dark">
                      {(Number(isTwin || isTwinSolid ? analyticsData.ai_mass_ground : analyticsData.estimated_mass_ground || 0) / 1000).toFixed(1)} т
                    </strong>
                  </div>
                </div>

                {(() => {
                  const g_empty = analyticsData.averages.ground.empty_sec || 0;
                  const g_full = analyticsData.averages.ground.full_sec || 0;
                  const g_unload = analyticsData.averages.ground.unload_sec || 0;
                  const g_actual = (g_empty + g_full + g_unload).toFixed(1);
                  const g_total_calc = parseFloat(g_actual) > 0 ? parseFloat(g_actual) : 1;

                  const pctEmpty = (g_empty / g_total_calc) * 100;
                  const pctFull = (g_full / g_total_calc) * 100;
                  const pctUnload = (g_unload / g_total_calc) * 100;

                  return (
                    <div className="mt-4">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <SafeTooltip content="Фактическое время цикла">
                          <span className="fw-bold text-dark small text-uppercase" style={hintStyle}>Ср. время цикла:</span>
                        </SafeTooltip>
                        <strong className="fs-5 text-dark">{g_actual} сек</strong>
                      </div>

                      <div className="d-flex w-100 rounded overflow-hidden mb-3 shadow-sm" style={{ height: "12px", backgroundColor: "#e9ecef" }}>
                        {pctEmpty > 0 && <div style={{ width: `${pctEmpty}%`, backgroundColor: "#3399ff" }} title={`Копание: ${g_empty}с`} />}
                        {pctFull > 0 && <div style={{ width: `${pctFull}%`, backgroundColor: "#ff9933" }} title={`Поворот: ${g_full}с`} />}
                        {pctUnload > 0 && <div style={{ width: `${pctUnload}%`, backgroundColor: "#2eb85c" }} title={`Разгрузка: ${g_unload}с`} />}
                      </div>

                      <div className="bg-light rounded p-3">
                        <div className="d-flex justify-content-between text-muted small mb-2">
                          <span className="d-flex align-items-center"><span className="d-inline-block rounded-circle me-2 shadow-sm" style={{ width: '10px', height: '10px', backgroundColor: '#3399ff' }}></span> Копание:</span>
                          <span className="fw-bold text-dark">{g_empty} сек</span>
                        </div>
                        <div className="d-flex justify-content-between text-muted small mb-2">
                          <span className="d-flex align-items-center"><span className="d-inline-block rounded-circle me-2 shadow-sm" style={{ width: '10px', height: '10px', backgroundColor: '#ff9933' }}></span> Поворот:</span>
                          <span className="fw-bold text-dark">{g_full} сек</span>
                        </div>
                        <div className="d-flex justify-content-between text-muted small mb-0">
                          <span className="d-flex align-items-center"><span className="d-inline-block rounded-circle me-2 shadow-sm" style={{ width: '10px', height: '10px', backgroundColor: '#2eb85c' }}></span> Разгрузка:</span>
                          <span className="fw-bold text-dark">{g_unload} сек</span>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      {analyticsData.use_digital_twin && (
        <CRow className="mb-4">
          <CCol xs={12}>
            <CCard className="shadow-sm border-info border-opacity-50 bg-info bg-opacity-10">
              <CCardBody className="py-2 d-flex flex-column flex-md-row justify-content-between align-items-center">
                <div className="d-flex align-items-center mb-2 mb-md-0">
                  <CIcon icon={cilInfo} className="text-info me-2" size="xl" />
                  <strong className="text-info">Показатели AI Цифрового двойника:</strong>
                </div>
                <div className="d-flex gap-4 small fw-bold text-dark">
                  <div>Всего: {Number(analyticsData.ai_volume_total || 0).toFixed(1)} м³ / {(Number(analyticsData.ai_mass_total || 0) / 1000).toFixed(1)} т</div>
                  <div className="d-none d-sm-block">В машину: {Number(analyticsData.ai_volume_vehicle || 0).toFixed(1)} м³</div>
                  <div className="d-none d-sm-block">В землю: {Number(analyticsData.ai_volume_ground || 0).toFixed(1)} м³</div>
                </div>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      )}

      {analyticsData.illegal_work && analyticsData.illegal_work.cycles > 0 && (
        <CRow className="mb-4">
          <CCol xs={12}>
            <CCard className="shadow-sm border-danger border-opacity-50 bg-danger bg-opacity-10">
              <CCardBody className="py-3 d-flex flex-column flex-md-row justify-content-between align-items-center">
                <div className="d-flex align-items-center mb-2 mb-md-0">
                  <span className="fs-3 me-3">🚨</span>
                  <div>
                    <strong className="text-danger fs-6">Зафиксирована работа вне заданной смены!</strong>
                    <div className="text-muted small">Данные операции не включаются в основную статистику.</div>
                  </div>
                </div>
                <div className="d-flex flex-wrap gap-4 small fw-bold text-dark align-items-center mt-2 mt-md-0">
                  <div>Ковшей: <span className="text-danger">{analyticsData.illegal_work.cycles} шт.</span></div>
                  <div>Объем: <span className="text-danger">{analyticsData.illegal_work.volume} м³</span></div>
                  <div>Масса: <span className="text-danger">{analyticsData.illegal_work.mass_tons} т</span></div>
                  <div>Время работы: <span className="text-danger">{formatDuration(analyticsData.illegal_work.duration_sec)}</span></div>
                </div>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      )}

      {/* --- КНОПКА ДЕТАЛИЗАЦИИ СМЕН И САМОСВАЛОВ (ВНИЗУ) --- */}
      <div className="text-center mt-4 mb-4 d-flex justify-content-center">
        <CButton
          color="primary"
          className="fw-bold shadow-sm d-flex align-items-center justify-content-center text-white"
          style={{ height: '38px', minWidth: '350px' }}
          onClick={() => setShowDetails(!showDetails)}
        >
          <CIcon icon={showDetails ? cilChevronTop : cilChevronBottom} className="me-2" />
          {showDetails ? "Скрыть детализацию" : "Развернуть детализацию (Смены и Самосвалы)"}
        </CButton>
      </div>

      {showDetails && (
        <>
          {analyticsData.has_multiple_shifts && (
            <div className="mt-4">
              <h5 className="text-primary border-bottom pb-2 mb-4">{t("analysis.shift_details")}</h5>
              <CRow>
                {Object.entries(analyticsData.shift_stats).map(([name, stats]) => {
                  let shiftFactCycles = 0; let shiftFactVol = 0; let shiftFactMass = 0; let hasShiftFact = false;
                  if (stats.truck_stats_summary) {
                    hasShiftFact = true;
                    Object.values(stats.truck_stats_summary).forEach((tTruck) => {
                      shiftFactCycles += tTruck.cycles || 0; shiftFactVol += Number(tTruck.vol) || 0; shiftFactMass += Number(tTruck.mass) || 0;
                    });
                  }

                  const shiftVolTotal = Number(
                    isTwinSolid ? ((stats.ai_volume_total ?? (stats.volume_total * aiVolRatio)) * (stats.volume_total > 0 ? stats.solid_volume_total / stats.volume_total : globalSolidRatio))
                      : isTwin ? (stats.ai_volume_total ?? (stats.volume_total * aiVolRatio))
                        : isSolid ? stats.solid_volume_total
                          : stats.volume_total || 0
                  ).toFixed(1);

                  const shiftVolVehicle = Number(
                    isTwinSolid ? ((stats.ai_volume_vehicle ?? (stats.volume_vehicle * aiVolRatio)) * (stats.volume_vehicle > 0 ? stats.solid_volume_vehicle / stats.volume_vehicle : globalSolidRatio))
                      : isTwin ? (stats.ai_volume_vehicle ?? (stats.volume_vehicle * aiVolRatio))
                        : isSolid ? stats.solid_volume_vehicle
                          : stats.volume_vehicle || 0
                  ).toFixed(1);

                  const shiftVolGround = Number(
                    isTwinSolid ? ((stats.ai_volume_ground ?? (stats.volume_ground * aiVolRatio)) * (stats.volume_ground > 0 ? stats.solid_volume_ground / stats.volume_ground : globalSolidRatio))
                      : isTwin ? (stats.ai_volume_ground ?? (stats.volume_ground * aiVolRatio))
                        : isSolid ? stats.solid_volume_ground
                          : stats.volume_ground || 0
                  ).toFixed(1);

                  const shiftMassTotal = (Number(isTwin || isTwinSolid ? (stats.ai_mass_total ?? (stats.mass_total * aiMassRatio)) : stats.mass_total || 0) / 1000).toFixed(1);
                  const shiftMassVehicle = (Number(isTwin || isTwinSolid ? (stats.ai_mass_vehicle ?? (stats.mass_vehicle * aiMassRatio)) : stats.mass_vehicle || 0) / 1000).toFixed(1);
                  const shiftMassGround = (Number(isTwin || isTwinSolid ? (stats.ai_mass_ground ?? (stats.mass_ground * aiMassRatio)) : stats.mass_ground || 0) / 1000).toFixed(1);

                  return (
                    <CCol md={6} key={name} className="mb-4">
                      <CCard className="h-100 shadow-sm border-top-primary border-top-3">
                        <CCardHeader className="bg-white fw-bold fs-5 text-primary">{name}</CCardHeader>
                        <CListGroup flush>
                          <CListGroupItem className="d-flex justify-content-between border-0 pb-1">
                            <SafeTooltip content={t("tooltips.shift_cycles")}><span className="text-muted" style={hintStyle}>{t("analysis.cycles_mixed")}</span></SafeTooltip>
                            <strong>{stats.cycles_total} /<span style={{ color: classIdToMeta[1].color }}> {stats.cycles_vehicle}{hasShiftFact && (<span className="text-muted fw-normal small"> ({t("analysis.fact")} {shiftFactCycles})</span>)}</span> /<span style={{ color: classIdToMeta[2].color }}> {stats.cycles_ground}</span></strong>
                          </CListGroupItem>

                          <CListGroupItem className="d-flex justify-content-between border-0 py-1">
                            <SafeTooltip content={t("tooltips.shift_volume")}>
                              <span className="text-muted" style={hintStyle}>{t("analysis.volume_m3")}</span>
                            </SafeTooltip>
                            <SafeTooltip
                              content={
                                <div className="small text-start">
                                  <div>{t("analysis.in_auto")} <strong>{shiftVolVehicle} м³</strong></div>
                                  {hasShiftFact && <div>{t("analysis.fact")} <strong>{shiftFactVol.toFixed(1)} м³</strong></div>}
                                  <div>{t("analysis.in_ground_short")} <strong>{shiftVolGround} м³</strong></div>
                                </div>
                              }
                            >
                              <strong style={{ borderBottom: "1px dashed #6c757d", cursor: "help" }}>
                                {shiftVolTotal}
                              </strong>
                            </SafeTooltip>
                          </CListGroupItem>

                          <CListGroupItem className="d-flex justify-content-between border-0 py-1">
                            <SafeTooltip content={t("tooltips.shift_mass")}>
                              <span className="text-muted" style={hintStyle}>{t("analysis.mass_t")}</span>
                            </SafeTooltip>
                            <SafeTooltip
                              content={
                                <div className="small text-start">
                                  <div>{t("analysis.in_auto")} <strong>{shiftMassVehicle} т</strong></div>
                                  {hasShiftFact && <div>{t("analysis.fact")} <strong>{(shiftFactMass / 1000).toFixed(1)} т</strong></div>}
                                  <div>{t("analysis.in_ground_short")} <strong>{shiftMassGround} т</strong></div>
                                </div>
                              }
                            >
                              <strong style={{ borderBottom: "1px dashed #6c757d", cursor: "help" }}>
                                {shiftMassTotal}
                              </strong>
                            </SafeTooltip>
                          </CListGroupItem>

                          <hr className="my-2" />
                          <CListGroupItem className="d-flex justify-content-between border-0 py-1">
                            <SafeTooltip content={t("tooltips.shift_dig_swing")}><span className="text-muted" style={hintStyle}>{t("analysis.dig_swing")}</span></SafeTooltip>
                            <strong>{formatDuration(stats.durations[3])} / {formatDuration(stats.durations[0])}</strong>
                          </CListGroupItem>
                          <CListGroupItem className="d-flex justify-content-between border-0 py-1">
                            <SafeTooltip content={t("tooltips.shift_unload")}><span className="text-muted" style={hintStyle}>{t("analysis.unload_mixed")}</span></SafeTooltip>
                            <strong>{formatDuration(stats.durations[1])} / {formatDuration(stats.durations[2])}</strong>
                          </CListGroupItem>
                          <CListGroupItem className="d-flex justify-content-between border-0 py-1">
                            <SafeTooltip content={t("tooltips.shift_downtime")}><span className="text-danger fw-bold" style={hintStyle}>{t("analysis.downtime_short")}</span></SafeTooltip>
                            <strong className="text-danger">{formatDuration(stats.actual_downtime)}</strong>
                          </CListGroupItem>
                          <CListGroupItem className="d-flex justify-content-between border-0 pt-1">
                            <span className="text-muted">{t("analysis.no_data_short")}</span><strong className="text-muted">{formatDuration(stats.no_data)}</strong>
                          </CListGroupItem>
                        </CListGroup>
                      </CCard>
                    </CCol>
                  );
                })}
              </CRow>
            </div>
          )}

          {analyticsData.truck_stats_summary && Object.keys(analyticsData.truck_stats_summary).length > 0 && !device?.hide_truck_stats && (
            <div className="mt-5">
              <div className="d-flex align-items-center mb-4">
                <CIcon icon={cilTruck} className="text-success me-3" size="xxl" />
                <h4 className="mb-0 fw-bold text-dark">{t("analysis.truck_stats_title", "Статистика по самосвалам")}</h4>
              </div>
              <CRow className="g-4">
                {Object.entries(analyticsData.truck_stats_summary).map(([label, stats]) => {
                  const truckSession = analyticsData.truck_sessions?.find((s) => s.label === label);
                  const truckColor = truckSession && truckSession.color ? truckSession.color : "#6c757d";

                  return (
                    <CCol md={6} xl={4} key={label}>
                      <CCard className="h-100 shadow-sm border-0" style={{ borderLeft: `4px solid ${truckColor}` }}>
                        <CCardHeader className="bg-white border-bottom-0 py-3 d-flex align-items-center">
                          <div
                            className="shadow-sm flex-shrink-0"
                            style={{ width: "16px", height: "16px", backgroundColor: truckColor, borderRadius: "50%", marginRight: "12px" }}
                          />
                          <h5 className="mb-0 fw-bold text-dark text-truncate" title={label}>{label}</h5>
                        </CCardHeader>
                        <CCardBody className="p-0 bg-white d-flex flex-column">
                          <div className="p-3 flex-grow-1">

                            <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                              <span className="text-muted small fw-semibold">🚩 {t("analysis.arrivals", "Подъездов")}:</span>
                              <strong className="text-dark fs-6">{stats.arrivals}</strong>
                            </div>

                            <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                              <span className="text-muted small fw-semibold">🏗️ {t("analysis.total_buckets", "Всего ковшей")}:</span>
                              <strong className="text-dark fs-6">{stats.cycles}</strong>
                            </div>

                            <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                              <span className="text-muted small fw-semibold">📊 {t("analysis.buckets_min_max_avg", "Мин / Макс / Ср")}:</span>
                              <strong className="text-primary">{stats.min_cycles} / {stats.max_cycles} / {stats.avg_cycles}</strong>
                            </div>

                            <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                              <span className="text-muted small fw-semibold">📦 {t("analysis.rock_volume", "Объем породы")}:</span>
                              <strong className="text-dark">{Number(
                                isTwinSolid ? ((stats.ai_vol ?? (stats.vol * aiVolRatio)) * (stats.vol > 0 ? stats.solid_vol / stats.vol : globalSolidRatio))
                                  : isTwin ? (stats.ai_vol ?? (stats.vol * aiVolRatio))
                                    : isSolid ? stats.solid_vol
                                      : stats.vol || 0
                              ).toFixed(1)} м³</strong>
                            </div>

                            <div className="d-flex justify-content-between align-items-center">
                              <span className="text-muted small fw-semibold">⚖️ {t("analysis.rock_mass", "Масса породы")}:</span>
                              <strong className="text-dark">{(Number(isTwin || isTwinSolid ? (stats.ai_mass ?? (stats.mass * aiMassRatio)) : stats.mass || 0) / 1000).toFixed(1)} т</strong>
                            </div>

                          </div>

                          <div className="px-3 pb-3 pt-2 bg-light mt-auto rounded-bottom">
                            <CButton
                              color="primary"
                              variant="outline"
                              size="sm"
                              className="w-100 fw-bold shadow-sm"
                              style={{ transition: 'all 0.2s' }}
                              onClick={() => {
                                const enrichedSessions = stats.sessions.map(baseSession => {
                                  const fullS = analyticsData.truck_sessions?.find(s => s.id === baseSession.id) || {};

                                  let finalVol = fullS.vol || 0;
                                  let finalMass = fullS.mass || 0;

                                  if (isTwinSolid) {
                                    const solidRatio = stats.vol > 0 ? (stats.solid_vol / stats.vol) : globalSolidRatio;
                                    finalVol = (finalVol * aiVolRatio) * solidRatio;
                                    finalMass = finalMass * aiMassRatio;
                                  } else if (isTwin) {
                                    finalVol = finalVol * aiVolRatio;
                                    finalMass = finalMass * aiMassRatio;
                                  } else if (isSolid) {
                                    const solidRatio = stats.vol > 0 ? (stats.solid_vol / stats.vol) : globalSolidRatio;
                                    finalVol = finalVol * solidRatio;
                                  }

                                  return {
                                    ...baseSession,
                                    vol: finalVol,
                                    mass: finalMass,
                                  };
                                });

                                openSessionsModal(label, enrichedSessions);
                              }}
                            >
                              {t("analysis.show_all_sessions", "Показать все заезды")} ({stats.arrivals})
                            </CButton>
                          </div>
                        </CCardBody>
                      </CCard>
                    </CCol>
                  );
                })}
              </CRow>
            </div>
          )}
        </>
      )}

      <DowntimeModal
        visible={downtimeModal.visible}
        onClose={() => setDowntimeModal({ visible: false, data: null })}
        deviceId={device?.id}
        initialData={downtimeModal.data}
        onSuccess={() => { }}
      />
    </div>
  );
};

export default AnalyticsDashboard;
